"use client";

import { useApolloClient } from "@apollo/client/react";
import { AnimatePresence, MotionConfig, motion } from "motion/react";
import { useLocale, useTranslations } from "next-intl";
import {
  Suspense,
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { base64ToBlob } from "@/components/widget/tabs/messages-tab/message-mapping";
import type { MockMessage, MockMessageMedia } from "@/dummy";
import { getMockThreads } from "@/dummy";
import { AI_AGENT_ROLE, AI_ASSISTANT_NAME, resolveAiName } from "@/lib/ai-assistant";
import {
  type ArticleTitleState,
  reduceArticleTitle,
  visibleArticleTitle,
} from "@/lib/article-title";
import { assetUrl } from "@/lib/asset-url";
import { closedFrameExtent } from "@/lib/closed-frame-extent";
import { downloadTextFile, safeFilenameStem } from "@/lib/download-file";
import { type GifIngestKind, useGiphyIngest } from "@/lib/giphy-client";
import type { ReactionSurface, VisitorPageInput } from "@/lib/graphql/generated/types";
import {
  VisitorHelpFoldersDocument,
  VisitorHelpListDocument,
} from "@/lib/graphql/queries/generated/help.generated";
import {
  type TrackVisitorButtonClickInput,
  type TrackVisitorLinkClickInput,
  type TrackVisitorPageViewInput,
  useMarkVisitorChatReadMutation,
  useResolveVisitorChatMutation,
  useTrackVisitorButtonClickMutation,
  useTrackVisitorLinkClickMutation,
  useTrackVisitorPageViewMutation,
  useUploadVisitorMediaMutation,
  useVisitorChatAssignmentQuery,
  useVisitorThreadsQuery,
  VisitorChatHistoryDocument,
  VisitorChatRecentInboundDocument,
  VisitorChatTranscriptDocument,
} from "@/lib/graphql/queries/generated/messaging.generated";
import { VisitorNewsListDocument } from "@/lib/graphql/queries/generated/news.generated";
import { useUpdateVisitorProfileMutation } from "@/lib/graphql/queries/generated/profile.generated";
import { useSubmitVisitorReactionMutation } from "@/lib/graphql/queries/generated/reactions.generated";
import { HIT_PAD, hitRects } from "@/lib/hit-area";
import { isIdentifiedVisitor } from "@/lib/identified-visitor";
import {
  CLOSED_ANCHOR_KINDS,
  closedFootprintBoxes,
  closedHitSurfaces,
  getAnchor,
} from "@/lib/launcher-anchors";
import { lazyChunk } from "@/lib/lazy-chunk";
import { isTrustedMessageSource } from "@/lib/message-source";
import { cacheKeys, cacheSet } from "@/lib/offline-cache";
import { openFrameSize } from "@/lib/open-frame-size";
import { OutboxProvider } from "@/lib/outbox-context";
import { whenPanelHidden } from "@/lib/panel-exit";
import { usePreview } from "@/lib/preview/use-preview";
import { usePreviewBridge } from "@/lib/preview/use-preview-bridge";
import { applyReadMark } from "@/lib/read-watermark";
import { LocalSlotsProvider } from "@/lib/targeting/local-slots-context";
import { effectiveCapabilities, hasCapability } from "@/lib/targeting/map-content";
import { pickVariant } from "@/lib/targeting/match";
import { SessionContentProvider } from "@/lib/targeting/session-content-context";
import { isThreadSeedMessage } from "@/lib/targeting/slot-seed";
import { slotIdOfThreadKey, variantIdOfThreadKey } from "@/lib/targeting/slot-thread";
import { useIframeHostViewport } from "@/lib/targeting/use-host-viewport-width";
import { useLocalSlotsState } from "@/lib/targeting/use-local-slots";
import { useMatchContextFor } from "@/lib/targeting/use-match-context";
import { useSessionContent } from "@/lib/targeting/use-session-content";
import { buildTranscript } from "@/lib/transcript";
import { useDemoContent } from "@/lib/use-demo-translator";
import { useNewsUnread } from "@/lib/use-news-unread";
import { useOutbox } from "@/lib/use-outbox";
import type { PasskeySessionResult } from "@/lib/use-passkey-auth";
import { useSoundPlayer } from "@/lib/use-sound-player";
import { useStableValue } from "@/lib/use-stable-value";
import { useThemeMode } from "@/lib/use-theme-mode";
import { useThreadsResync } from "@/lib/use-threads-resync";
import { useTurnstile } from "@/lib/use-turnstile";
import {
  NETWORK_UNREACHABLE,
  persistIdentifiedSession,
  resetStoredVisitorSession,
  useHostVisitorData,
  useVisitorAuth,
  type VisitorSession,
} from "@/lib/use-visitor-auth";
import { useVisitorProfilePreferences } from "@/lib/use-visitor-profile-preferences";
import type { RealtimeMessageEvent } from "@/lib/use-visitor-realtime";
import {
  type ThreadUpdateEvent,
  useVisitorThreadsRealtime,
} from "@/lib/use-visitor-threads-realtime";
import type { UnreadPeekMessage } from "@/lib/use-visitor-unread";
import { useWidgetConfig } from "@/lib/use-widget-config";
import { useWidgetEnv, type VisitorIdentity } from "@/lib/use-widget-env";
import { useWidgetUnavailable } from "@/lib/use-widget-unavailable";
import { cn } from "@/lib/utils";
import { type Assignment, assignmentFromQuery, EMPTY_ASSIGNMENT } from "@/lib/visitor-assignment";
import { captureVisitorPage } from "@/lib/visitor-page/capture-visitor-page";
import { useSendVisitorMessage } from "@/lib/visitor-page/use-send-visitor-message";
import { createMemoryStorage, type VisitorStorage } from "@/lib/visitor-storage";
import type { MessageSlot, WidgetTab } from "@/lib/widget-config";
import { useWidgetInstance } from "@/lib/widget-instance-context";
import {
  enabledTabCount,
  openRelatedArticle,
  pickInitialTab,
  popRoute,
  routeShowsTabs,
  routeToTab,
  type WidgetRoute,
  withOrigin,
} from "@/lib/widget-route";
import { mediaTypeIcon } from "@/lib/widget-thread";
import {
  applyThreadLive,
  pruneThreadLive,
  reduceThreadLive,
  type ThreadLive,
  toThreadList,
} from "@/lib/widget-thread-adapter";
import { BottomNav } from "../bottom-nav";
import { ConnectionFooter } from "../connection-footer";
import { ConversationHeader } from "../conversation-header";
import { DemoMessages } from "../demo-messages";
import { HelpCategoryView } from "../help-category-view";
import { withDemoSlotThreads } from "../launcher-message/demo-slot-threads";
import { slotListRow } from "../launcher-message/slot-list-row";
import { slotPeekMessage } from "../launcher-message/slot-peek";
import {
  type PendingSlotReply,
  useLauncherActions,
} from "../launcher-message/use-launcher-actions";
import { useLauncherMessage } from "../launcher-message/use-launcher-message";
import { LauncherPeek } from "../launcher-peek";
import { TabTitleHeader } from "../tab-title-header";
import { HelpTab } from "../tabs/help-tab";
import { HomeTab } from "../tabs/home-tab";
import { MessagesTab } from "../tabs/messages-tab";
import { clearDraftSessions } from "../tabs/messages-tab/composer/draft-session";
import { ConversationSkeleton } from "../tabs/messages-tab/conversation-skeleton";
import { mapHistoryToMessages } from "../tabs/messages-tab/message-mapping";
import { ThreadList } from "../tabs/messages-tab/thread-list";
import { NewsTab } from "../tabs/news-tab";
import { ProfileTab } from "../tabs/profile-tab";
import { TrackingProvider } from "../tracking/tracking-provider";
import { WidgetLauncher } from "../widget-launcher";
import { announceThreadFrame } from "./announce-thread-frame";
import { AuthErrorPopup } from "./auth-error-overlay";
import { countRealUnread } from "./client-welcome";
import { consumeDeletedLast } from "./deleted-last-message";
import { expandHasRoom } from "./expand-has-room";
import {
  isPermanentBackendError,
  normaliseThemeMode,
  resolveOrgLogo,
  routeKey,
  routeRank,
  routeVariants,
} from "./helpers";
import { HomeBackdrop } from "./home-backdrop";
import { applyMessageDelete, applyMessageUpdate, reconcileOutboxOps } from "./live-messages";
import {
  isRememberableMessagesRoute,
  type RememberedMessagesRoute,
  resumeMessagesRoute,
} from "./messages-resume";
import type { NotificationLabels } from "./notification-text";
import { notificationTitle } from "./notification-title";
import { openConversationChatId, resyncAssignment } from "./open-conversation";
import { createOpenSizeReporter } from "./open-size-report";
import { peekSendInput, peekVisitorBubble } from "./peek-send";
import { previewRoute, previewTabSignalKey } from "./preview-route";
import {
  discardOptimisticProfile,
  isProfileOp,
  restoreServerLanguage,
} from "./profile-drop-recovery";
import { recentInboundFromFrame } from "./recent-inbound";
import { RouteLayer } from "./route-layer";
import { regularThreadMismatchRetry, slotNotFoundFallback } from "./slot-not-found-fallback";
import {
  createAnnounceState,
  isReadingThread,
  seedAnnounceState,
  takeAnnouncement,
} from "./thread-announce";
import { useAuthPopup } from "./use-auth-popup";
import { useDefaultOpen } from "./use-default-open";
import { useDeferredOpen } from "./use-deferred-open";
import { useHostOpen } from "./use-host-open";
import { useIframeHostControl } from "./use-iframe-host-control";
import { routeForDefaultTab, useInitialRoute } from "./use-initial-route";
import { useLauncherOffset } from "./use-launcher-offset";
import { useLocaleDirection } from "./use-locale-direction";
import { useOpenCloseEvents } from "./use-open-close-events";
import { useOpenConversationStream } from "./use-open-conversation-stream";
import { usePanelFocus } from "./use-panel-focus";
import { usePlaceMemory } from "./use-place-memory";
import { useReadWatermark } from "./use-read-watermark";
import { useSessionMemoryReset } from "./use-session-memory-reset";
import { useVisitorBlocked } from "./use-visitor-blocked";

// The article views render Markdown (`react-markdown` + the
// micromark/mdast stack, ~80 KB combined). They're drill-in routes
// behind navigation, so `lazy()` keeps that stack out of the initial
// payload — it streams in the first time a visitor opens an article.
const HelpArticleView = lazyChunk("help-article", () => import("../help-article-view"), {
  pick: (m) => m.HelpArticleView,
});
const NewsArticleView = lazyChunk("news-article", () => import("../news-article-view"), {
  pick: (m) => m.NewsArticleView,
});

/** localStorage key for the visitor's panel expand/collapse preference.
 *  Persisted (not session) so a visitor who likes the wide panel keeps
 *  it across reloads + revisits. */
const PANEL_EXPANDED_KEY = "wexio:panel-expanded";

/** How long a thread-list "typing…" indicator survives without a fresh typing
 *  ping before it self-clears. The BE re-sends `typing:true` every ~4s while
 *  active but doesn't guarantee a `typing:false`, so we expire it client-side.
 *  6s gives comfortable margin over the ~4s cadence. */
const TYPING_DECAY_MS = 6000;

/** Viewport margins the panel reserves — mirror the `max-w` / `max-h`
 *  Tailwind clamps on the panel (`100vw - 2rem`, `100dvh - 7rem`). Used
 *  to clamp the ANIMATION target to the size that actually renders, so
 *  width + height settle together instead of the height stopping early
 *  at its ceiling. Keep in sync with the className. */
const PANEL_MARGIN_X = 32; // 2rem
const PANEL_MARGIN_Y = 112; // 7rem

/** Read the persisted expand preference. Defensive against storage being
 *  unavailable (private mode) + SSR (no `window`) → defaults collapsed. */
function readExpandedPref(storage: VisitorStorage): boolean {
  if (typeof window === "undefined") return false;
  try {
    return storage.getItem(PANEL_EXPANDED_KEY) === "1";
  } catch {
    return false;
  }
}

/** localStorage key prefix for "the visitor has seen the operator's welcome
 *  MESSAGE" — persisted per integration so the proactive welcome peek + its
 *  launcher badge show ONCE and don't resurface on every reload once read. */
const WELCOME_READ_KEY_PREFIX = "wexio:welcome-read:";
const NO_MESSAGE_SLOTS: readonly MessageSlot[] = [];
const welcomeReadKey = (publicKey: string | null): string =>
  `${WELCOME_READ_KEY_PREFIX}${publicKey ?? ""}`;
function readWelcomeRead(publicKey: string | null, storage: VisitorStorage): boolean {
  if (typeof window === "undefined") return false;
  try {
    return storage.getItem(welcomeReadKey(publicKey)) === "1";
  } catch {
    return false;
  }
}

/** One attachment on a queued message. Either ALREADY uploaded (`mediaId` +
 *  canonical `s3Url`) or PENDING upload (`dataBase64` — the raw bytes, so a file
 *  attached OFFLINE can be uploaded later at drain time). `run` resolves pending
 *  items to a `mediaId` (uploading first) and patches the op so a retry won't
 *  re-upload. `alt`/`mimetype` render the optimistic bubble. */
interface OutboxMediaItem {
  filename: string;
  mimetype: string;
  mediaId?: string;
  s3Url?: string;
  dataBase64?: string;
  /** A picked Giphy asset not yet re-hosted. `run` ingests it at drain
   *  (uploadVisitorMediaFromUrl) → mediaId, so the bubble shows instantly from
   *  `previewUrl` while the ingest happens in the background. */
  giphyUrl?: string;
  gifKind?: GifIngestKind;
  /** Giphy preview rendition — the optimistic bubble's image until the ingest
   *  resolves the stored one. */
  previewUrl?: string;
  /** Intrinsic size of the Giphy preview — reserves the optimistic bubble's
   *  aspect-ratio box so it never jumps when the preview paints. */
  previewWidth?: number;
  previewHeight?: number;
}

/** Payload of a queued "sendMessage" outbox op — a visitor text / quick-reply /
 *  media send. `chatId` is only carried to detect a new-thread first send
 *  (adopting the ack's real chatId); the BE resolves the target chat from the
 *  JWT + `threadKey`, so it isn't sent in the mutation. */
interface OutboxMessagePayload {
  text: string;
  threadKey: string | null;
  chatId: string | null;
  buttonValue?: string;
  /** First-send seed of a `message:<slotId>` thread (015). */
  messageSlot?: { slotId: string; variantId: string; locale?: string };
  /** Host page captured when the visitor sent (not at replay). */
  page?: VisitorPageInput;
  media?: OutboxMediaItem[];
}

/**
 * With Apollo's `errorPolicy: "all"` (see `apollo-client.ts`), a GraphQL error
 * does NOT reject the mutation promise — it lands in `result.errors` while the
 * promise resolves. The outbox drives keep/drop off a THROW, so a swallowed
 * error would look like success (or, for a message, a generic "empty ack" that
 * reads as transient and retries forever). This rethrows any `errors` as one
 * Error carrying `graphQLErrors`, the shape `isPermanentBackendError` classifies
 * (BAD_USER_INPUT / 4xx → permanent drop; anything else → transient retry).
 */
function throwOnGraphQLErrors(result: { errors?: readonly unknown[] | null }): void {
  const errors = result.errors;
  if (!errors || errors.length === 0) return;
  const first = errors[0] as { message?: string } | undefined;
  const err = new Error(first?.message ?? "backend error") as Error & { graphQLErrors?: unknown };
  err.graphQLErrors = errors;
  throw err;
}

/** Shared fallback while a lazily-loaded drill-in view streams in. */
function RouteChunkFallback() {
  return (
    <div className="flex flex-1 items-center justify-center">
      <div className="border-wx-border border-t-wx-primary h-5 w-5 animate-spin rounded-full border-2" />
    </div>
  );
}

/**
 * Top-level widget shell — owns the navigation stack + decides which
 * surfaces render.
 *
 * **Two layout modes** (route-driven):
 *   - **List routes** (`home` / `help` / `news`)        — bottom nav
 *     + tab-style headers. Visitor is browsing an index.
 *   - **Drill-in routes** (`messages` / `help-category` /
 *     `help-article`)                                    — no bottom
 *     nav. Sticky header with a back button + per-route title.
 *
 * **Home tab is special**: the header chrome (logo + avatars + mute +
 * close) scrolls WITH the hero so the brand gradient backdrop reads
 * as one continuous surface. Other tabs render a sticky strip header.
 *
 * **Motion**: panel slide-in with spring; per-route content crossfades
 * through `AnimatePresence`; launcher icon morph via the FAB.
 */
export function WidgetShell() {
  // Demo content (`messages/demo/*.json`) is lazily imported + merged
  // into i18n only in demo mode by `DemoMessages` — it stays out of the
  // production bundle every visitor downloads.
  //
  // `reducedMotion="user"`: with `prefers-reduced-motion: reduce` every motion
  // transform animation (panel slide / scale, FAB morph, badge pulse, peek pop-in)
  // jumps to its end value; opacity still fades, so nothing blinks in or out. CSS
  // `animate-*` utilities and transitions are not Motion's: the `prefers-reduced-motion`
  // block at the end of app/globals.css stops those (typing dots, pulse, ping).
  return (
    <MotionConfig reducedMotion="user">
      <DemoMessages>
        <WidgetShellInner />
      </DemoMessages>
    </MotionConfig>
  );
}

function WidgetShellInner() {
  const tHeader = useTranslations("header");
  const tLauncher = useTranslations("launcher");
  const tHome = useTranslations("home");
  const tTranscript = useTranslations("transcript");
  const tNotification = useTranslations("notification");
  // Read from inside long-lived socket callbacks: a ref keeps them on the
  // CURRENT locale without re-subscribing when the translator identity changes.
  const notificationLabelsRef = useRef<NotificationLabels>({
    newMessage: "",
    image: "",
    video: "",
    voiceMessage: "",
    attachment: "",
  });
  notificationLabelsRef.current = {
    newMessage: tNotification("newMessage"),
    image: tNotification("image"),
    video: tNotification("video"),
    voiceMessage: tNotification("voiceMessage"),
    attachment: tNotification("attachment"),
  };
  // Mock thread-list content for demo mode. Read straight from the bundled
  // `demo.<locale>.json` (see `useDemoContent`) instead of next-intl's `demo`
  // namespace — the latter isn't visible to the builders in an embedded host
  // (dashboard preview), which left the previews empty + logged MISSING_MESSAGE.
  const tDemo = useDemoContent();
  // Visitor's active language — threaded into article-detail views so
  // their content queries fetch the right locale (was hardcoded "en").
  const locale = useLocale();
  const env = useWidgetEnv();
  // This widget's own connection state (public key, token, Apollo client): two widgets on a page
  // must not share it.
  const instance = useWidgetInstance();
  // BE preview token (BE 062): its request channel and, for the iframe, its postMessage input.
  usePreviewBridge();
  const preview = usePreview();

  const resolved = useWidgetConfig();
  const { config, isLoading: configLoading, error: configError } = resolved;
  // Fail CLOSED (owner policy, docs/CONFIG_REFERENCE.md "Failure policy"): a definitive "off"
  // answer from the config / gate, a BE status other than `active`, or a 403
  // `WEB_MESSENGER_NOT_AVAILABLE` / `WEB_MESSENGER_DISABLED` on any visitor request hides the widget AND
  // stops everything. Treating a hidden widget as non-production demo switches off every
  // production-gated effect below (visitor handshake, SSE, tracking, outbox, content queries).
  const { unavailable, evicted } = useWidgetUnavailable(instance);
  const hidden =
    unavailable || configError !== null || (!configLoading && config.status !== "active");
  const mode = hidden ? "demo" : resolved.mode;
  const isDummy = hidden ? true : resolved.isDummy;
  // Content (news / help / home / `previewData`) follows `isDummy`. Session-bound surfaces (profile,
  // threads, messages, reactions, read-acks, unread, outbox, SSE) also go demo in a preview that
  // will never get a visitor session (no `previewToken`): see `useWidgetConfig`.
  const sessionDummy = isDummy || resolved.sessionDemo;
  // Tell the iframe loader to drop its frame, but only once the eviction is done (see the hook).
  const hiddenReported = hidden && (!unavailable || evicted);
  useEffect(() => {
    if (hiddenReported) env.onHidden?.();
  }, [hiddenReported, env]);
  // The positive counterpart: the iframe loader shows its frame only after this.
  const activeReported = !hidden && !configLoading;
  useEffect(() => {
    if (activeReported) env.onActive?.();
  }, [activeReported, env]);
  // Operator's saved mode preference flows in here. BE serialises the
  // enum in uppercase (`AUTO` / `LIGHT` / `DARK`); normalise to the
  // lowercase set the widget's CSS / data-theme attribute expects.
  // `useThemeMode` still respects URL + postMessage overrides, the
  // visitor's localStorage pref (when operator allows `auto`), and
  // the visitor's OS preference as fallbacks.
  // Theme + logos are resolved BELOW (after `session` is defined) — the
  // visitor profile query needs `session.token` and we want it threaded into
  // `useThemeMode` directly. See the block right after the `session` const.
  // Which corner the launcher + panel anchor to (operator-configurable;
  // defaults to bottom-right).
  const launcherLeft = config.theme?.launcherPosition === "LEFT_BOTTOM";
  // React-component embed → anchor to the host `<div>` (`absolute` +
  // %-based clamps) instead of the viewport (`fixed` + vw/dvh clamps),
  // so the widget stays inside its container. See `WidgetEnv.embedded`.
  const embedded = env.embedded ?? false;
  const { muted, toggleMuted, playInbound, playOutbound } = useSoundPlayer(config.sounds);
  // Host-supplied visitor data (embeds set it on `env`; the iframe
  // receives it over postMessage). `identity` (verified, userHash) routes
  // the handshake to the identified flow; `prefill` (unverified profile)
  // pre-fills / auto-attaches the prechat.
  const { identity: visitorIdentity, prefill: visitorPrefill } = useHostVisitorData(env);
  // Identity the visitor established INSIDE the widget (e.g. tapped the
  // Google sign-in button) — distinct from `visitorIdentity` which the
  // HOST supplies. Host identity wins; this fills in when the host didn't
  // identify and the visitor signs in themselves.
  const [widgetIdentity, setWidgetIdentity] = useState<VisitorIdentity | null>(null);
  // Bumped by `Wexio('shutdown')` (logout / shared device) to force a
  // fresh anonymous handshake after the stored session is wiped.
  const [sessionEpoch, setSessionEpoch] = useState(0);
  // Bumped ONLY by shutdown (a new person): drives the in-memory wipe, unlike `sessionEpoch`
  // which a plain re-handshake (retry / reconnect poll) also bumps.
  const [shutdownEpoch, setShutdownEpoch] = useState(0);
  // Wired to the outbox's `clear` further down (the outbox is created after this handler).
  const clearOutboxRef = useRef<() => void>(() => {});
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onMessage = (e: MessageEvent) => {
      if (!isTrustedMessageSource(e, env.kind)) return;
      if (!e.data || e.data.type !== "wexio:shutdown:v1") return;
      // Forget the stored visitorId + token + any in-widget sign-in, then
      // re-handshake fresh. The new chatId clears `liveMessages` via the
      // effect below, so the next person on a shared device never sees
      // the prior chat.
      resetStoredVisitorSession(instance);
      // The next person on a shared device must not see (or send under their own token) what the
      // previous visitor left unsent: the composer drafts and the offline outbox.
      clearDraftSessions(instance.drafts);
      clearOutboxRef.current();
      setWidgetIdentity(null);
      setPasskeySession(null);
      setShutdownEpoch((n) => n + 1);
      setSessionEpoch((n) => n + 1);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [env.kind, instance]);
  // Cloudflare Turnstile — fetches a fresh single-use token before
  // each visitor-auth handshake (and the prechat submit, downstream).
  // `siteKey: null` ⇒ Turnstile disabled for the integration; the
  // hook's `getToken` resolves to `undefined` and the BE accepts the
  // mutation without a token.
  //
  // Defence in depth against a misconfigured integration: if the
  // operator's config has `enabled: true` but `siteKey: null` (or empty
  // string), we treat it as DISABLED. Cloudflare's `render()` would
  // throw if we passed it a missing siteKey, and the widget would hang
  // forever waiting for a token that can never be minted. Better to
  // skip the check entirely + log a warning so the operator notices
  // their config is broken; the BE-side enforcement still rejects the
  // request if it really needs a token, surfacing as a normal
  // handshake error the visitor can retry through.
  const turnstileConfigured = !!config.botProtection?.turnstile?.enabled;
  const turnstileSiteKeyRaw = config.botProtection?.turnstile?.siteKey ?? null;
  const turnstileSiteKey = turnstileConfigured && turnstileSiteKeyRaw ? turnstileSiteKeyRaw : null;
  // Dev-only warning so a misconfigured integration is loud about it.
  useEffect(() => {
    if (turnstileConfigured && !turnstileSiteKeyRaw && process.env.NODE_ENV !== "production") {
      console.warn(
        "[wexio] Turnstile is enabled in the integration config but no `siteKey` was supplied — skipping bot-protection check. Add the Cloudflare site key in the operator dashboard or disable Turnstile.",
      );
    }
  }, [turnstileConfigured, turnstileSiteKeyRaw]);
  const { getToken: getTurnstileToken } = useTurnstile({
    siteKey: turnstileSiteKey,
    // Anchor the challenge container to the same corner as the
    // launcher — when Cloudflare forces a visible challenge, it
    // appears directly above the FAB instead of mid-screen.
    position: launcherLeft ? "left" : "right",
  });

  const baseSession = useVisitorAuth({
    // Wait for config to land BEFORE firing the handshake — otherwise
    // a config that turns Turnstile on races against the initial
    // anonymous handshake which would go out without a token and get
    // rejected by the BE. Once `configLoading` flips false, the
    // resolved `getTurnstileToken` is the one paired with the real
    // siteKey (or `undefined` if Turnstile is off).
    enabled: !configLoading && mode === "production" && !isDummy,
    identity: visitorIdentity ?? widgetIdentity,
    epoch: sessionEpoch,
    getTurnstileToken,
  });
  // Passkey produces a full verified session out-of-band (a 2-step
  // WebAuthn ceremony, not an identity token), so it can't route through
  // `useVisitorAuth`'s identity handshake like Google does. The hook
  // already stamped the token into Apollo; here we adopt the rest as a
  // session OVERRIDE so the UI reads `verified` + the new `chatId`.
  // Cleared on shutdown so the next person falls back to a fresh
  // anonymous handshake.
  const [passkeySession, setPasskeySession] = useState<VisitorSession | null>(null);
  const session = passkeySession ?? baseSession;
  // Session-dependent welcome / Home content (contract v1). The visitor token changes on
  // handshake, restore, re-auth and logout, which refetches; pre-chat submit and profile save call
  // `refresh()` from the context. Legacy configs (no `contentVersion`) never call the endpoint.
  const sessionContent = useSessionContent({
    publicKey: instance.publicKey,
    token: session.token,
    locale,
    anonymous: !!session.token && !session.verified,
    visitorKey: session.token ? `${session.peopleId ?? ""}:${session.verified}` : null,
    pageKind: env.kind,
    enabled:
      mode === "production" &&
      !isDummy &&
      !!instance.publicKey &&
      (config.contentVersion ?? 0) >= 1,
  });

  // Signed in / identified: no sign-in buttons, and the pre-chat only asks for what is still unknown.
  // Derived from props and session on every render, so a preview flipping between Anonymous and
  // Signed in is live (no remount).
  const identifiedVisitor = isIdentifiedVisitor({
    verified: session.verified,
    sessionVisitors: sessionContent.visitors,
    override: env.audienceOverride?.visitors,
  });

  // Live network reachability — one signal for the connection footer AND the
  // auth-error gate below. Offline is a connectivity STATE (shown in the
  // footer), not a sign-in failure, so we must not surface the auth-error
  // overlay just because a handshake (e.g. StartAnonymousVisitor) failed with
  // no network.
  const [online, setOnline] = useState<boolean>(() =>
    typeof navigator === "undefined" ? true : navigator.onLine,
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  // When connectivity returns AND the last handshake had errored (it failed
  // offline), re-fire it by bumping the auth epoch — so the session finally
  // establishes without the visitor having to reload. Guarded to the
  // offline→online transition so a persistently-failing online handshake can't
  // loop (a failed retry re-sets the same error without re-triggering here).
  const wasOnlineRef = useRef(online);
  useEffect(() => {
    const was = wasOnlineRef.current;
    wasOnlineRef.current = online;
    if (!was && online && session.error) setSessionEpoch((n) => n + 1);
  }, [online, session.error]);

  // Auth-error popup state (floats above the launcher with retry +
  // close). Hidden by default — only appears when the visitor taps
  // the launcher AND there's an active error. Auto-closes when the
  // error resolves (Retry succeeded) so it doesn't linger.
  //
  // Launcher spinner state: tied to `isAuthenticating` so any handshake
  // (initial OR retry) puts the launcher in a "connecting" state.
  // Without this, a visitor can tap the launcher mid-handshake and
  // open a half-loaded panel.
  // Suppressed when the BE is unreachable (server down / offline): that's a
  // connectivity state, NOT a sign-in failure — showing "Couldn't connect /
  // error signing you in" there is wrong + alarming. `network_unreachable`
  // covers "BE down but browser online" (navigator.onLine can't detect that);
  // the `online` gate additionally covers a truly-offline browser. Real auth
  // failures (4xx / bad response) still surface the overlay. It re-arms once a
  // handshake finally succeeds; the retry effects below keep re-attempting.
  const isUnreachable = session.error === NETWORK_UNREACHABLE;
  const showAuthError =
    !!session.error && !isUnreachable && mode === "production" && !isDummy && online;
  // While the BE is unreachable, poll the handshake (bump the epoch) so the
  // session establishes as soon as the server is back — no reload needed.
  // Re-arms after each attempt (epoch in deps); stops on success (error clears)
  // or a real auth error (error changes off the sentinel).
  useEffect(() => {
    if (session.error !== NETWORK_UNREACHABLE) return;
    const t = setTimeout(() => setSessionEpoch((n) => n + 1), 6000);
    return () => clearTimeout(t);
  }, [session.error]);
  // Spinner during ANY pre-ready phase: config still fetching, or
  // visitor-auth handshake (initial OR retry) in flight. Without the
  // `configLoading` clause, a fast tap right after page load could
  // open the launcher BEFORE the handshake has even started — config
  // fetch and handshake are sequential.
  // `awaitingFirstAuth` closes the gap between "config loaded" and "the handshake flag is set" (an effect sets it):
  // for one committed render neither is true, and a host open request would slip through as if auth were fine.
  const authSettledOnce = useRef(false);
  if (session.token || session.error) authSettledOnce.current = true;
  const awaitingFirstAuth = !authSettledOnce.current;
  const showAuthSpinner =
    (configLoading || session.isAuthenticating || awaitingFirstAuth) &&
    mode === "production" &&
    !isDummy;
  const bumpSessionEpoch = useCallback(() => setSessionEpoch((n) => n + 1), []);
  const {
    open: authPopupOpen,
    show: showAuthPopup,
    dismiss: dismissAuthPopup,
    retry: handleAuthRetry,
  } = useAuthPopup({
    hasError: showAuthError,
    hasSession: !!session.token,
    onRetry: bumpSessionEpoch,
  });

  // Hard block on opening the panel while auth is failing OR mid-flight.
  // Every entry point (launcher tap, peek tap, notification click,
  // future host imperative `window.WexioWidget.show()`) routes through
  // these helpers so the guard applies uniformly. Closing is ALWAYS
  // allowed — the visitor can still bail out of a panel that's open.
  //
  // Refs are updated in render scope (NOT a useEffect) so the guards
  // see the current value on the same render the error / loading
  // state appears — a useEffect would lag one tick behind and a fast
  // tap could slip through. Two refs: one to know we're blocked at
  // all (used by both openPanel + togglePanel), one to disambiguate
  // "blocked because errored" (open the popup) vs "blocked because
  // loading" (ignore silently).
  const authBlockedRef = useRef(false);
  const authErroredRef = useRef(false);
  // `configLoading` too: before the config answers the mode is still the "demo" placeholder, so the spinner flag is off.
  const openBlocked = showAuthError || showAuthSpinner || configLoading;
  authBlockedRef.current = openBlocked;
  authErroredRef.current = showAuthError;
  // `env.open` / `env.defaultOpen`: host-chosen initial state (controlled value wins).
  // Not open at first paint while config / the handshake is loading or auth failed: `useDefaultOpen` /
  // `useHostOpen` request it through the same guarded open as a launcher tap (deferred, or the retry popup).
  const [isOpen, setIsOpen] = useState(
    (env.open ?? env.defaultOpen ?? false) && !authBlockedRef.current,
  );
  const openPanel = useCallback(() => {
    if (authErroredRef.current) {
      showAuthPopup();
      return;
    }
    if (authBlockedRef.current) return;
    setIsOpen(true);
  }, [showAuthPopup]);
  // Host open requests (defaultOpen replay, `Wexio('open')`, controlled `open`, `previewTab`) wait for the
  // widget to be able to open instead of being dropped while config / the handshake load. An auth
  // ERROR still goes straight to `openPanel` (retry popup). The latest intent wins (`cancel` on close).
  const deferredOpen = useDeferredOpen(openBlocked, openPanel, showAuthError, instance.pendingOpen);
  const requestOpen = deferredOpen.request;
  const hostOpen = useCallback(
    () => (authErroredRef.current ? openPanel() : requestOpen()),
    [openPanel, requestOpen],
  );
  const cancelOpen = deferredOpen.cancel;
  const hostClose = useCallback(() => {
    cancelOpen();
    setIsOpen(false);
  }, [cancelOpen]);
  const togglePanel = useCallback(() => {
    setIsOpen((prev) => {
      if (prev) return false; // closing always allowed
      if (authErroredRef.current) {
        // Visitor tapped the launcher while auth is broken — surface
        // the retry popup instead of silently swallowing the tap.
        showAuthPopup();
        return false;
      }
      if (authBlockedRef.current) return false; // loading; ignore
      return true;
    });
  }, [showAuthPopup]);

  // Visitor's BE-persisted profile preferences (theme mode + language +
  // desktop-notifications opt-in). Skipped when Profile is disabled or the
  // visitor isn't authed yet — the query needs a visitor JWT. The language
  // preference is persisted to a cookie inside the hook; mid-session locale
  // change requires a remount which is out of scope.
  const profilePrefs = useVisitorProfilePreferences({
    enabled: !!config.features.profile && (sessionDummy || !!session.token),
    isDummy: sessionDummy,
  });

  // DevTools helpers exposed on `window`:
  //
  //   - `__wexioNotifyDebug()` — dump the state the next inbound will
  //     evaluate against (pref + permission + prefs-loaded + session).
  //   - `__wexioTestNotify()`  — fire `new Notification(...)` DIRECTLY
  //     with hard-coded copy, bypassing the entire inbound pipeline.
  //     If THIS doesn't show on screen either, the popup is being eaten
  //     by macOS / Chrome (Focus mode, alert style = None, Chrome
  //     "quieter notifications", etc.) — not our code.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const w = window as unknown as {
      __wexioNotifyDebug?: () => unknown;
      __wexioTestNotify?: () => unknown;
    };
    w.__wexioNotifyDebug = () => {
      const state = {
        prefEnabled: profilePrefs.desktopNotifications,
        permission: typeof Notification !== "undefined" ? Notification.permission : "(unsupported)",
        prefsLoaded: profilePrefs.loaded,
        featureEnabled: !!config.features.profile,
        sessionToken: !!session.token,
      };
      console.log("[wexio] notify state:", state);
      return state;
    };
    w.__wexioTestNotify = () => {
      if (typeof Notification === "undefined") {
        console.warn("[wexio] Notification API not available in this context");
        return "unsupported";
      }
      if (Notification.permission !== "granted") {
        console.warn("[wexio] permission not granted:", Notification.permission);
        return Notification.permission;
      }
      try {
        const n = new Notification("Wexio test", {
          body: "If you can see this, notifications work.",
          tag: "wexio-test",
        });
        n.onshow = () => console.log("[wexio] test notification: onshow fired");
        n.onerror = (e) => console.warn("[wexio] test notification: onerror", e);
        n.onclose = () => console.log("[wexio] test notification: onclose");
        console.log("[wexio] test notification posted to browser");
        return "posted";
      } catch (err) {
        console.warn("[wexio] test notification threw:", err);
        return err;
      }
    };
  }, [
    profilePrefs.desktopNotifications,
    profilePrefs.loaded,
    config.features.profile,
    session.token,
  ]);
  // Theme — operator preference + visitor profile preference (when
  // `themeModeEditable`). See `useThemeMode` for the full precedence chain.
  const resolvedThemeMode = useThemeMode(normaliseThemeMode(config.themeMode), {
    preference: profilePrefs.themeMode,
    allowed: profilePrefs.themeModeEditable,
  });
  // Resolve the operator's per-mode logo down to a single URL the header
  // components render. Prefer the active mode's logo; fall back to the
  // other mode's (one logo is better than none); `null` when the operator
  // uploaded neither — the header then shows the framed Wexio fallback mark.
  const organizationLogoUrl = resolveOrgLogo(config.organizationLogo, resolvedThemeMode);
  // Conversation-header logo. Demo has no operator logo, so fall back to
  // the theme-matched Wexio mark (`logo-light.png` in light mode,
  // `logo-dark.png` in dark) so the header reads as branded rather than
  // blank. Scoped to the conversation header — the home header keeps its
  // own `WexioMark` placeholder. CDN-aware via `assetUrl`.
  // Skip the demo logo fallback when the host EXPLICITLY configured the org
  // logo (the playground always sends `organizationLogo`, even as `null`) — so
  // deliberately clearing the logo doesn't snap back to the bundled mark.
  const orgLogoConfigured = env.configOverride?.organizationLogo !== undefined;
  const conversationLogoUrl =
    organizationLogoUrl ??
    (isDummy && !orgLogoConfigured ? assetUrl(`/logo-${resolvedThemeMode}.png`) : null);

  const initialRoute = useCallback(
    () => routeForDefaultTab(config.defaultTab, config.features),
    [config.defaultTab, config.features],
  );
  const [route, setRoute] = useState<WidgetRoute>(initialRoute);
  // Lands on the RESOLVED config's `defaultTab` (the first render only has the demo's).
  useInitialRoute(route, setRoute, {
    defaultTab: config.defaultTab,
    features: config.features,
    loading: configLoading,
  });

  // Drill into a sub-view (article, category…) remembering the CURRENT view as
  // the back target, so "Back" returns exactly where the visitor came from
  // (home / messages / a category / another article) rather than a fixed
  // section index. Tab switches keep using `setRoute` directly — they're a
  // fresh context, not a drill-in.
  const drillTo = useCallback(
    (next: WidgetRoute) => setRoute((prev) => withOrigin(next, prev)),
    [],
  );

  // Host-driven preview navigation (the dev playground): jump to the requested
  // tab LIVE when `env.previewTab` changes — no remount. The signal is an
  // object so re-selecting the same tab still re-issues navigation (its `nonce`
  // changes the reference). A request for a disabled tab falls back to the
  // first enabled one, mirroring the initial-route logic; `messages` resumes
  // straight into the latest thread. It also OPENS the panel (via `openPanel`,
  // so the same auth guards apply) — otherwise a closed widget wouldn't surface
  // the tab the operator is editing. Real embeds never set this, so the effect
  // is inert there. `openPanel` is a stable `useCallback`.
  useHostOpen(env.open, hostOpen, hostClose);
  useDefaultOpen(env.open, env.defaultOpen, hostOpen);
  // Script-tag/iframe embed: `Wexio('open' | 'close' | 'navigate')` arrive as messages.
  const iframeNav = useIframeHostControl(env.kind, hostOpen, hostClose);
  const previewTabSignal = env.previewTab ?? iframeNav;
  // Keyed by VALUE (`previewTabKey`), not by object identity: a host that re-renders
  // with a fresh object carrying the same `nonce` must not yank the visitor back.
  const previewTabKey = previewTabSignal ? previewTabSignalKey(previewTabSignal) : null;
  // biome-ignore lint/correctness/useExhaustiveDependencies: navigate only when the signal's value changes — the signal object, `config.features` + stable `openPanel` are read inside, not triggers
  useEffect(() => {
    if (!previewTabSignal) return;
    setRoute(previewRoute(previewTabSignal, config.features));
    hostOpen();
  }, [previewTabKey]);

  // Initialised from localStorage so the preference survives reloads /
  // revisits. Safe to read in the initializer (no hydration mismatch):
  // the panel is closed at first paint, so `expanded` doesn't affect any
  // SSR-rendered DOM until the visitor opens + expands it.
  const [expanded, setExpanded] = useState(() => readExpandedPref(instance.storage));
  // Persist the preference on every change. Writing the initial value
  // back is a harmless no-op (it equals what we just read).
  useEffect(() => {
    try {
      instance.storage.setItem(PANEL_EXPANDED_KEY, expanded ? "1" : "0");
    } catch {
      // Private mode / blocked storage — keep it in-memory this session.
    }
  }, [expanded, instance.storage]);
  // Currently-visible article title — fed by the help/news article
  // views via `onTitleResolved`. Shown in the sticky header for
  // article routes so the bar reads as a breadcrumb ("← Article
  // name") instead of the parent tab nameplate ("News" / "Help").
  // `null` while the article is still loading; the header falls
  // back to the tab title until the article view resolves.
  //
  // We DON'T clear this here on route change. The previous version
  // had a route-watching `useEffect` that scheduled `setArticleTitle(
  // null)` on every nav — but the article view's own `useEffect`
  // (which calls `onTitleResolved`) fires on the same tick when the
  // Apollo cache returns instantly on a re-visit. React's batching
  // could resolve `null` last, dropping the title back to "News"
  // permanently. Clearing is now owned by the article view itself
  // via a cleanup function: it sets the title on mount, clears it
  // on unmount. That's deterministic regardless of cache state.
  const [articleTitleState, setArticleTitleState] = useState<ArticleTitleState>(null);
  const currentRouteKey = routeKey(route);
  // One sink per route key (stable between renders of the same route): an
  // exiting article keeps reporting under ITS key, so its late clear cannot
  // wipe the next (related) article's title.
  const articleTitleSink = useMemo(
    () => (title: string | null) =>
      setArticleTitleState((prev) => reduceArticleTitle(prev, currentRouteKey, title)),
    [currentRouteKey],
  );
  const articleTitle = visibleArticleTitle(articleTitleState, currentRouteKey);
  // Stays `true` after the first open. Used to gate the route
  // content — when the panel has never been opened, none of the
  // tab components mount, so their GraphQL queries + SSE
  // subscriptions stay dormant. Only the launcher unread query
  // (and the visitor-auth handshake it depends on) runs while
  // closed.
  const [hasOpened, setHasOpened] = useState(false);
  useEffect(() => {
    if (isOpen) setHasOpened(true);
  }, [isOpen]);
  // Host-facing open / close hooks, fired on every transition whichever control
  // caused it. The in-DOM embeds (React, web-component) report in EVERY mode: the
  // callbacks are the host's own (a controlled `open` must sync in the dashboard
  // preview too). The iframe forwards them to the loader as host window events, so
  // it stays `production` only: the dashboard preview must not fire host analytics.
  useOpenCloseEvents({
    isOpen,
    enabled: env.kind !== "iframe" || mode === "production",
    onOpen: env.onOpen,
    onClose: env.onClose,
  });

  // News read/unread — a countless dot on the launcher + News tab when the
  // newest post hasn't been seen. Production only (localStorage-backed,
  // publicKey-scoped) and only when the News tab is enabled.
  const news = useNewsUnread({
    enabled: mode === "production" && !isDummy && !!config.features.news,
    locale,
  });

  // ─── Shell-level SSE pump ─────────────────────────────────────
  // Listens for realtime events across the whole widget lifetime —
  // not just while MessagesTab is mounted — so the launcher peek +
  // home-tab `recent-message` block refresh as soon as an operator /
  // bot / AI message arrives. MessagesTab still handles its own
  // transient state (typing indicator, in-flight composer) but
  // session-durable state (`liveMessages`) lives here so a visitor
  // who switches Help → Messages and back doesn't lose the new
  // bubbles they just saw.
  const apolloClient = useApolloClient();
  const messagesTabHandlerRef = useRef<((e: RealtimeMessageEvent) => void) | null>(null);

  // Messages appended in-session (SSE-delivered inbound + visitor's
  // optimistic outbound). Merged with the `visitorChatHistory` query
  // result inside MessagesTab — kept here so route changes don't drop
  // them.
  const [liveMessages, setLiveMessages] = useState<MockMessage[]>([]);
  // Operator-handoff status: null (AI-only) / "pending" (assigned, not
  // yet accepted) / "connected" (operator accepted). Seeded once from
  // `visitorChatAssignment` below and kept current by the `assignment`
  // SSE branch in the pump.
  const [assignment, setAssignment] = useState<Assignment>(EMPTY_ASSIGNMENT);
  // GLOBAL (contact-level) block — the whole visitor is blocked: no composer in
  // any thread + no new conversation. A live `blockScope: "CONTACT"` SSE event
  // sets this tri-state (true/false); `null` = no live value yet, so we fall
  // back to the load-time `visitorBlocked` off the threads page (derived below).
  // Kept separate so a stale threads refetch can't clobber a fresher live value.
  const [liveContactBlock, setLiveContactBlock] = useState<boolean | null>(null);
  // Set the moment a live `assignment` SSE event has applied an update.
  // Guards the seed effect below against clobbering a fresher live status
  // with a stale `network-only` query snapshot that resolves after the SSE
  // event landed (the query is in flight from mount; SSE can beat it).
  const assignmentTouchedBySseRef = useRef(false);
  // Demo-only: a launcher-peek quick-reply tapped before the panel
  // opened. Handed to MessagesTab, which "sends" it on mount; cleared
  // once consumed.
  const [pendingPeekReply, setPendingPeekReply] = useState<string | null>(null);
  // A quick reply tapped on a slot's launcher message: the slot thread sends the label once mounted.
  const [pendingSlotReply, setPendingSlotReply] = useState<PendingSlotReply | null>(null);
  // Which thread the launcher-peek's last message belongs to, so tapping the
  // peek reopens THAT conversation (not the bare list). Set by whichever stream
  // delivered the inbound: the main pump → the anchor chat; the second stream →
  // the open topic thread. A ref (read only at tap time; no re-render needed).
  const peekTargetRef = useRef<{ chatId: string | null; threadKey: string | null } | null>(null);

  // ─── Realtime: one long-lived stream + the open conversation's ─────────
  // The visitor-level threads stream (below) is the only long-lived one: badge, unread, peek, sound,
  // notifications, Home block. A per-chat stream (`useOpenConversationStream`) is held only while a
  // conversation is on screen with the panel open and feeds just that view.
  //
  //   - `openKeyedThreadKey`: the open thread's key iff it's a keyed topic
  //     thread. Stable across chatId adoption (survives the first send), so the
  //     live buffer isn't wiped mid-thread. `null` for the anchor / list / home.
  //   - `activeConvId`: identity of the currently-open conversation; resets the
  //     single live buffer on a real conversation switch (never on adoption).
  const openKeyedThreadKey =
    route.kind === "messages-thread" && route.threadKey ? route.threadKey : null;
  const activeConvId = openKeyedThreadKey ?? "main";
  // The chatId of the OPEN conversation, for the per-thread operator-handoff
  // banner. Assignment is hybrid on the BE — PENDING cascades conversation-wide
  // but CONNECTED is per-chat (a topic thread can be CONNECTED while the main
  // stays in queue) — so the banner is keyed to the thread the visitor is in.
  // `null` on the list / a not-yet-created new thread (no banner there).
  const openConvChatId = route.kind === "messages-thread" ? (route.chatId ?? null) : null;

  // Reset the live-message buffer + handoff banner when the OPEN conversation
  // changes (each keeps its own in-session bubbles; the banner is per-thread, so
  // switching threads must not carry the previous thread's status).
  // A new session (shutdown) resets it too: the previous visitor's bubbles must not stay on screen
  // when the re-handshake hands back the same chatId.
  // biome-ignore lint/correctness/useExhaustiveDependencies: activeConvId and sessionEpoch are deliberate reset triggers, not unused deps
  useEffect(() => {
    setLiveMessages([]);
    setAssignment(EMPTY_ASSIGNMENT);
    assignmentTouchedBySseRef.current = false;
  }, [activeConvId, sessionEpoch]);

  // `threadsActive` = the visitor is looking at the Messages area. It now ONLY
  // gates the resume-to-latest effect; the `visitorThreads` query + the
  // visitor-level `threadUpdated` stream run WIDGET-WIDE (always-on once authed,
  // below), because they're the single source of truth for the launcher/tab
  // badge + peek + list dots — a topic-thread inbound must light the launcher
  // even for a visitor who never opened the widget.
  const threadsActive = isOpen && (route.kind === "messages" || route.kind === "messages-thread");

  // Topic-thread list (multi-topic threads). Runs whenever the
  // widget is mounted + authed — NOT only in Messages — because it's now the
  // single source of truth for unread EVERYWHERE: the thread-list dots, AND the
  // launcher + tab badge (total unread across all threads) + the launcher peek.
  // Gating it to "in Messages" left the badge blind to a topic thread's inbound
  // whenever the visitor wasn't looking at that thread. `cache-and-network` so a
  // reopen paints the cache instantly, then reconciles. BASE snapshot; live
  // per-thread status is folded over it below.
  const threadsQuery = useVisitorThreadsQuery({
    skip: sessionDummy || !session.token,
    fetchPolicy: "cache-and-network",
  });

  // ─── Live thread-list status ─────────────────────────────────────────
  // The visitor-level SSE stream (`/api/web/realtime/threads`) pushes per-thread
  // `threadUpdated` events (unread / preview / typing / assignment), keyed by
  // threadId (= chatId). We fold them into `threadLive` and merge over the base
  // query so the list is live — no polling. The stream's thread set is fixed at
  // connect, so `threadsEpoch` bumps to reconnect after a NEW thread is created.
  const [threadLive, setThreadLive] = useState<Record<string, ThreadLive>>({});
  // Refetch the list and let the result supersede live content that predates the request. Also the
  // recovery after a window in which live frames were lost (stream reopened, network back, tab
  // visible again): without it a missed update stayed hidden until a reload.
  const resyncThreads = useThreadsResync({
    enabled: !sessionDummy && !!session.token,
    refetch: async () => {
      const res = await threadsQuery.refetch();
      if (res?.error) throw res.error;
    },
    onSettled: (startedAt) => setThreadLive((prev) => pruneThreadLive(prev, startedAt)),
  });
  const refetchThreads = useCallback(() => {
    void resyncThreads();
  }, [resyncThreads]);
  // DEMO-only session read set: chat ids the visitor has opened (= read) this
  // session. Demo has no BE read-ack, so this is the source of truth that zeros
  // a thread's unread everywhere (launcher badge + peek, tab badge, list dot,
  // the thread itself) once it's been opened — persists for the mount.
  // Preview / demo without a BE: the message-slot threads the visitor has talked in, kept in memory (015).
  const [demoSlotThreads, setDemoSlotThreads] = useState<
    Record<string, { title: string; at: string }>
  >({});
  const [demoReadThreads, setDemoReadThreads] = useState<ReadonlySet<string>>(() => new Set());
  const [threadsEpoch, setThreadsEpoch] = useState(0);
  // The chatId a bare-visitor first send JUST lazily created + adopted. The
  // Messages tab remounts on the "" -> real id swap; on that mount it does a
  // catch-up history refetch to pull any greeting the FIRST_VISIT flow published
  // to the new chat's channel before the per-chat SSE subscribed. See
  // `catchUpHistory`.
  const [adoptedChatId, setAdoptedChatId] = useState<string | null>(null);
  // Sound + desktop-notification for a topic-thread inbound that NO per-chat
  // pump covers. The per-chat pumps only alert for the anchor (`session.chatId`,
  // main pump) and the OPEN topic thread (`openTopicChatId`, second stream) — so
  // a reply landing on a topic thread the visitor isn't in (list / launcher /
  // widget closed) was silent even though the badge lit up. This stream is the
  // widget-wide source of truth for those, so it owns the alert for that case.
  // `alertedThreadMsgRef` dedupes by the message's own timestamp so a repeated
  // frame (typing→message, read, reconnect replay) can't re-alert; it's seeded
  // ONCE from the initial thread list so pre-existing unreads don't chime on
  // connect (only genuinely-new arrivals do).
  const announceStateRef = useRef(createAnnounceState());
  // Per thread, the id of the last message the stream told us about (the query has no ids): a
  // `lastMessageDeletedId` frame is matched against it.
  const lastMessageIdsRef = useRef<Record<string, string>>({});
  // Per-thread typing-decay timers. The BE streams `typing:{active:true}`
  // repeatedly WHILE someone types but doesn't reliably send `typing:false`
  // when they stop — so a typing indicator would stick forever. Each fresh
  // typing ping (re)arms a timer that clears it if none arrives within the
  // window; a message/`typing:false`/explicit clear cancels it.
  const typingTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  useEffect(() => {
    const timers = typingTimersRef.current;
    return () => {
      for (const id of Object.values(timers)) clearTimeout(id);
    };
  }, []);
  // New person on this device (`Wexio('shutdown')`): drop everything held in memory for the previous
  // visitor, otherwise their thread list, unread badge and open view stay until B's data replaces them.
  useSessionMemoryReset(shutdownEpoch, () => {
    setThreadLive({});
    setDemoReadThreads(new Set());
    setAdoptedChatId(null);
    setLiveContactBlock(null);
    setRoute(initialRoute());
    announceStateRef.current = createAnnounceState();
    lastMessageIdsRef.current = {};
    // In place: the unmount cleanup above holds this same object.
    for (const [threadId, id] of Object.entries(typingTimersRef.current)) {
      clearTimeout(id);
      delete typingTimersRef.current[threadId];
    }
  });
  // NOTE: we deliberately KEEP `threadLive` across leaving/re-entering Messages.
  // It holds the latest live truth (e.g. a thread the visitor just READ → unread
  // 0); clearing it made re-entry fall back to the stale Apollo cache (unread 1)
  // for a beat, which churned the unread dot + the conversation's unread-divider
  // (flash in → dismiss). Typing self-expires via its own decay + the unmount
  // cleanup above, so no leave-time teardown is needed here.
  useVisitorThreadsRealtime({
    token: session.token,
    visitorId: session.peopleId,
    enabled: !sessionDummy && mode === "production" && !!session.token,
    epoch: threadsEpoch,
    onReopen: refetchThreads,
    onUpdate: (frame) => {
      // The last message was deleted: refetch the list (below) and let the row drop its held preview.
      // A deletion of an OLDER message leaves the row alone. Never announces (no `lastMessageFromVisitor`).
      const { frame: e, refetch: refetchForDeleted } = consumeDeletedLast(
        lastMessageIdsRef.current,
        frame,
        threads.find((t) => t.chatId === frame.threadId)?.lastMessageId,
      );
      // CONSUME boundary — the raw threadUpdated frame + which row it keys to.
      // The BE thread seed (the slot text, a BOT message the visitor already saw) is absorbed silently:
      // no unread, no sound, no notification.
      const seedFrame =
        e.lastMessageFromVisitor === false &&
        isThreadSeedMessage(
          { text: e.lastMessagePreview, senderKind: e.lastMessageSenderKind ?? "bot" },
          messageSlots,
        );
      setThreadLive((prev) => ({
        ...prev,
        [e.threadId]: reduceThreadLive(prev[e.threadId], seedFrame ? { ...e, unReadCount: 0 } : e),
      }));

      // A CONTACT-scope block change is GLOBAL — flip the visitor-wide lock
      // (per-THREAD scope is folded onto the row by `reduceThreadLive` above).
      if (e.blocked != null && e.blockScope === "CONTACT") setLiveContactBlock(e.blocked);

      // Typing auto-expiry (see `typingTimersRef`): re-arm on a fresh typing
      // ping; cancel on anything else (the reduce above already flips typing
      // off for a message arrival / explicit typing:false).
      const timers = typingTimersRef.current;
      if (timers[e.threadId]) {
        clearTimeout(timers[e.threadId]);
        delete timers[e.threadId];
      }
      if (e.typing?.active) {
        timers[e.threadId] = setTimeout(() => {
          delete timers[e.threadId];
          setThreadLive((prev) =>
            prev[e.threadId]
              ? { ...prev, [e.threadId]: { ...prev[e.threadId], typing: false } }
              : prev,
          );
        }, TYPING_DECAY_MS);
      }

      // ── Sound, notification, Home block and peek target ──
      // The threads stream is the ONLY source of these, in every state (panel closed, tab hidden, any
      // route), for the anchor and topic threads alike. `takeAnnouncement` dedupes on `lastMessageId`
      // (a reconnect, a resync or an edit never announces again).
      if (refetchForDeleted) refetchThreads();
      if (takeAnnouncement(announceStateRef.current, e, seedFrame)) {
        const threadItem = threads.find((t) => t.chatId === e.threadId);
        announceThreadFrame(e, {
          playInbound,
          markPeekTarget: () => {
            peekTargetRef.current = {
              chatId: e.threadId,
              threadKey: threadItem?.threadKey ?? null,
            };
          },
          writeRecent: () => writeRecentInbound(e),
          notify: {
            enabled: profilePrefs.desktopNotifications,
            reading: isReadingThread({
              isOpen,
              docVisible: typeof document !== "undefined" && !document.hidden,
              route,
              threadId: e.threadId,
              anchorChatId: session.chatId,
            }),
            title: notificationTitle(threadItem, threadAiName),
            labels: notificationLabelsRef.current,
            orgLogoUrl: organizationLogoUrl,
            open: () => {
              openPanel();
              // Reopen the exact thread the reply landed on.
              drillTo({
                kind: "messages-thread",
                chatId: e.threadId,
                threadKey: threadItem?.threadKey ?? null,
              });
            },
          },
        });
      }
    },
    onAdd: (a) => {
      // A lean "new thread exists" discovery frame (operator-initiated via
      // startWebConversation, cross-device, or our own first message). It
      // carries no render fields, so DEDUPE on chatId then refetch to pull the
      // row: ignore a chatId we already hold — already in the list, already
      // live-folded, or the anchor chatId we just adopted from our OWN send ack
      // (that path already surfaced the thread) — otherwise a `visitorThreads`
      // refetch materialises it (including an operator's first message as the
      // preview). The server already subscribed the new thread's channel, so its
      // subsequent activity arrives as ordinary `threadUpdated` frames.
      if (!a.chatId) return;
      const alreadyHeld =
        a.chatId === session.chatId ||
        !!threadLive[a.chatId] ||
        threads.some((t) => t.chatId === a.chatId);
      if (alreadyHeld) return;
      refetchThreads();
      // If the visitor is sitting on the (chatless) ANCHOR thread when an
      // operator proactively starts the conversation (`startWebConversation`
      // creates the anchor thread — no threadKey), adopt its chatId into the
      // open view so the operator's first message renders live in the thread
      // they're looking at, and trigger the same subscribe-race catch-up refetch
      // used for the visitor's own first send. Gated to the anchor thread so a
      // topic / cross-device thread never hijacks a specific open conversation.
      if (!a.threadKey && route.kind === "messages-thread" && !route.chatId && !route.threadKey) {
        setRoute((r) =>
          r.kind === "messages-thread" && !r.chatId ? { ...r, chatId: a.chatId } : r,
        );
        setAdoptedChatId(a.chatId);
      }
    },
  });

  // Seed the alert dedup set ONCE from the first thread-list payload so
  // pre-existing unreads don't chime on connect — only arrivals AFTER load do.
  useEffect(() => {
    if (announceStateRef.current.seeded) return;
    if (sessionDummy || !threadsQuery.data) return;
    seedAnnounceState(
      announceStateRef.current,
      toThreadList(threadsQuery.data, AI_ASSISTANT_NAME).map((t) => ({
        chatId: t.chatId,
        at: t.preview?.at ?? t.updatedAt ?? "",
      })),
    );
  }, [sessionDummy, threadsQuery.data]);

  // The AI persona name for thread rows (operator-configurable) — falls back to
  // the "AI Agent" role label when the operator disabled the agent identity.
  const threadAiName = resolveAiName(config.messenger?.aiAssistant) ?? AI_AGENT_ROLE;
  // Persisted "welcome MESSAGE seen" — set the moment the visitor opens the
  // welcome thread. Zeroes the DEFAULT thread's unread (below) so its peek +
  // launcher badge appear ONCE and never resurface; persisted so a reload
  // doesn't re-nag.
  const [welcomeRead, setWelcomeRead] = useState<boolean>(() =>
    readWelcomeRead(instance.publicKey, instance.storage),
  );
  const markWelcomeRead = useCallback(() => {
    setWelcomeRead(true);
    try {
      instance.storage.setItem(welcomeReadKey(instance.publicKey), "1");
    } catch {
      // Private mode / blocked storage — the in-memory flag still holds for the
      // session; a later reload may re-show it once. Acceptable.
    }
  }, [instance]);
  // The DEFAULT thread's preview text — the operator's welcome MESSAGE, else the
  // first HINT/SYSTEM. When the BE hasn't created any thread yet (a captured /
  // brand-new visitor) but the operator configured intro content, we synthesise
  // a single default thread from it so the visitor has somewhere to land + read.
  // Contract-v1 MESSAGE slots drive the launcher message and the launcher target (015). The legacy
  // flat list then no longer feeds a synthetic default thread: the slot bubble replaces its peek.
  const messageSlots = sessionContent.slots?.messageSlots ?? config.messenger?.messageSlots;
  // BE capability: a slot thread is per variant (`message:<slotId>:<variantId>`).
  const variantThreads = hasCapability(
    effectiveCapabilities(sessionContent.capabilities, config.capabilities),
    "variantThreads",
  );
  // Local slots (host props / unsaved preview / deleted on the BE): their thread is `slot-<id>`.
  const sessionSlotIds = useMemo(
    () =>
      sessionContent.slots ? new Set(sessionContent.slots.messageSlots.map((sl) => sl.id)) : null,
    [sessionContent.slots],
  );
  const localSlots = useLocalSlotsState({
    enabled: !sessionDummy,
    storage: instance.storage,
    publicKey: instance.publicKey,
    injected: resolved.injectedSlotIds,
    sessionIds: sessionSlotIds,
    variantThreads,
  });
  const slotLauncher =
    !!config.features.messenger && !!messageSlots?.some((s) => s.kind === "MESSAGE");
  const defaultThreadText = slotLauncher
    ? null
    : (config.messenger?.messages?.find((m) => m.kind === "MESSAGE")?.text ??
      config.messenger?.messages?.find((m) => m.kind === "SYSTEM" || m.kind === "HINT")?.text ??
      null);
  // Stable creation instant for the synthetic default thread (a fresh `new Date`
  // each recompute would churn recency sorting).
  const defaultThreadAtRef = useRef<string>(new Date().toISOString());
  // Debounce the synthetic default thread: surface it only after a short grace
  // period with the BE reporting ZERO threads. A matched FIRST_VISIT flow's
  // greeting lands as a REAL thread ~a second after handshake — the grace lets
  // it supersede the client default so the visitor sees ONE launcher peek (the
  // flow's), not the default peek followed a beat later by the flow peek. If no
  // real thread arrives, the default arms + shows. The IN-THREAD welcome bubble
  // is NOT gated on this (it renders from config), so opening Messages is never
  // delayed — only the proactive launcher peek waits out the grace.
  const threadsResolved = !sessionDummy && !threadsQuery.loading && threadsQuery.data !== undefined;
  const noRealThreads = (threadsQuery.data?.visitorThreads?.threads?.length ?? 0) === 0;
  const [defaultThreadArmed, setDefaultThreadArmed] = useState(false);
  useEffect(() => {
    if (sessionDummy || !defaultThreadText || !threadsResolved || !noRealThreads) {
      setDefaultThreadArmed(false);
      return;
    }
    const id = setTimeout(() => setDefaultThreadArmed(true), 1500);
    return () => clearTimeout(id);
  }, [sessionDummy, defaultThreadText, threadsResolved, noRealThreads]);
  // Local read marks: a message already read stays read when a refetch / stream reconnect brings the
  // BE's stale unread count back (a rejected or still-queued read-ack, e.g. for a blocked visitor).
  const readWatermark = useReadWatermark({
    publicKey: instance.publicKey,
    storage: instance.storage,
  });
  const threads = useMemo(() => {
    if (sessionDummy) {
      // Demo has no BE, so `getMockThreads` returns fresh rows (unreadCount
      // baked in) on every recompute — a `threadLive` overlay would be
      // clobbered. We track "read in this session" separately in
      // `demoReadThreads` and zero those rows' unread here so the badge/peek
      // clear the moment the visitor opens a thread (see the effect below).
      const demo = getMockThreads(tDemo, threadAiName).map((item) =>
        demoReadThreads.has(item.chatId) ? { ...item, unreadCount: 0 } : item,
      );
      // With message slots the opened slot threads are added beside the demo conversations (015).
      return slotLauncher ? withDemoSlotThreads(demo, demoSlotThreads, threadAiName) : demo;
    }
    const base = toThreadList(threadsQuery.data, threadAiName);
    // No real thread yet, but the operator configured intro content → surface a
    // single DEFAULT (main) thread so the list/peek/badge/resume all have a row
    // to work with. `chatId: ""` = not yet created on the BE; the first send
    // adopts the real chatId. Unread is 1 until the visitor reads it.
    if (base.length === 0 && defaultThreadText && defaultThreadArmed) {
      return [
        {
          chatId: "",
          threadKey: null,
          status: "OPEN" as const,
          unreadCount: welcomeRead ? 0 : 1,
          updatedAt: defaultThreadAtRef.current,
          preview: { text: defaultThreadText, at: defaultThreadAtRef.current, fromVisitor: false },
          senderName: threadAiName,
          lastSenderKind: "ai",
        },
      ];
    }
    const merged = base.map((item) =>
      applyReadMark(applyThreadLive(item, threadLive[item.chatId]), readWatermark.marks),
    );
    // Live updates can change recency → keep the list most-recent-first.
    const recency = (t: (typeof merged)[number]) => Date.parse(t.preview?.at ?? t.updatedAt) || 0;
    return merged.sort((a, b) => recency(b) - recency(a));
  }, [
    sessionDummy,
    threadsQuery.data,
    threadLive,
    threadAiName,
    tDemo,
    demoReadThreads,
    demoSlotThreads,
    slotLauncher,
    defaultThreadText,
    defaultThreadArmed,
    welcomeRead,
    readWatermark.marks,
  ]);
  // Status of the OPEN conversation, read off the live-overlaid thread list
  // (the threads SSE keeps it current, so an operator CLOSE flips it live).
  // Drives the composer lock in MessagesTab — only CLOSED hides the composer;
  // RESOLVED keeps it (a visitor reply reopens). `null` for a not-yet-created
  // thread (no chatId to match) → treated as open.
  const openConvResolvedChatId =
    route.kind === "messages-thread"
      ? (route.chatId ?? (route.threadKey ? null : session.chatId))
      : null;
  const openThreadStatus = openConvResolvedChatId
    ? (threads.find((tt) => tt.chatId === openConvResolvedChatId)?.status ?? null)
    : null;
  // GLOBAL block — a live CONTACT event wins; otherwise the load-time
  // `visitorBlocked` off the threads page. Drives: no composer anywhere + no new
  // conversation.
  const visitorBlocked = useVisitorBlocked({
    publicKey: instance.publicKey,
    storage: instance.storage,
    live: liveContactBlock,
    threads: threadsQuery.data?.visitorThreads?.visitorBlocked ?? null,
    sessionContent: sessionContent.blocked ?? null,
    handshake: session.blocked === true,
  });
  // Block state for the OPEN conversation: globally blocked OR this thread's own
  // `blocked` flag (live-overlaid). Drives the composer lock for that thread.
  const openThreadBlocked =
    visitorBlocked ||
    (openConvResolvedChatId
      ? (threads.find((tt) => tt.chatId === openConvResolvedChatId)?.blocked ?? false)
      : false);
  // First thread fetch in flight with nothing cached → show a skeleton in the
  // list instead of flashing the empty state.
  const threadsLoading = !sessionDummy && threadsQuery.loading && !threadsQuery.data;
  // Unread count of the OPEN conversation — drives MessagesTab's read-ack + the
  // "unread messages" divider. MUST be per-thread: the launcher's main-scoped
  // `unread.count` would make the ack gate skip `markVisitorChatRead` on a TOPIC
  // thread whenever the main chat happens to be read (0), leaving that thread
  // stuck unread on the list. Reads the merged list first, then the LIVE overlay
  // directly — a freshly-created thread isn't in `visitorThreads` yet (so it's
  // absent from the list), but its `threadUpdated` unread already lives in
  // `threadLive`; without this fallback its ack gate + divider would see 0.
  const openThreadUnread = openConvChatId
    ? (threads.find((t) => t.chatId === openConvChatId)?.unreadCount ??
      threadLive[openConvChatId]?.unreadCount ??
      0)
    : 0;

  // Optimistic read-clear for the OPEN thread — the tab's ack calls this so the
  // launcher/tab badge (`totalUnread`, derived from the thread list) drops the
  // instant the visitor reads, without waiting for the `markVisitorChatRead`
  // round-trip + the `threadUpdated{0}` echo (which reconciles it shortly
  // after). Zeroes the open thread's live unread; a no-op off a thread.
  const threadsRef = useRef(threads);
  threadsRef.current = threads;
  const markThreadRead = readWatermark.markRead;
  const clearOpenThreadUnread = useCallback(() => {
    const cid = openConvChatId;
    if (!cid) return;
    markThreadRead(cid, threadsRef.current.find((t) => t.chatId === cid)?.preview?.at);
    setThreadLive((prev) => {
      if (prev[cid]?.unreadCount === 0) return prev;
      return { ...prev, [cid]: { ...(prev[cid] ?? {}), unreadCount: 0, contentAt: Date.now() } };
    });
  }, [openConvChatId, markThreadRead]);

  // Demo has no BE read-ack, so the tab's "read once scrolled to the bottom"
  // gate never fires the mutation — and the launcher badge + peek would stick
  // forever. In demo, OPENING a thread IS reading it: record it as read the
  // moment it's the open conversation so the threads memo zeroes its unread and
  // the badge/peek clear like they would against a real backend.
  useEffect(() => {
    if (!sessionDummy || !openConvChatId) return;
    const cid = openConvChatId;
    setDemoReadThreads((prev) => {
      if (prev.has(cid)) return prev;
      const next = new Set(prev);
      next.add(cid);
      return next;
    });
  }, [sessionDummy, openConvChatId]);

  // ─── Unified badge + peek (single source: the thread list) ───────────
  // The launcher/tab badge is the TOTAL unread across ALL threads, and the
  // launcher peek is the most-recently-active unread thread's preview — both
  // from the same `threadUpdated`-fed list as the dots, so a topic thread's
  // inbound lights the badge/peek even when the visitor isn't in that thread,
  // and reading it (→ `threadUpdated{0}`) clears them.
  // The client-generated welcome (synthetic default thread) is not an operator / flow message, so it
  // does not light the badge (see `client-welcome`).
  const totalUnread = countRealUnread(threads);
  // Peek dismissal is keyed by chatId+time, so a NEWER message re-shows the peek
  // after the visitor dismissed the previous one.
  const [dismissedPeekKey, setDismissedPeekKey] = useState<string | null>(null);
  const peekThread = (
    slotLauncher && sessionDummy && env.launcherMessagePreview === "visible" ? [] : threads
  ).find((t) => t.unreadCount > 0 && t.preview && !t.preview.fromVisitor);
  const peekKey = peekThread?.preview ? `${peekThread.chatId}:${peekThread.preview.at}` : null;
  const launcherPeek: UnreadPeekMessage | null =
    peekThread?.preview && peekKey && peekKey !== dismissedPeekKey
      ? {
          id: peekKey,
          // Media last message: when there's a THUMBNAIL the picture speaks for
          // itself, so show only the caption (if any), no redundant label. With
          // no thumb (audio/voice/file), show a type glyph + caption/"Attachment".
          // Plain text → the text.
          text: !peekThread.preview.media
            ? peekThread.preview.text
            : peekThread.preview.mediaThumbUrl
              ? peekThread.preview.text || null
              : `${mediaTypeIcon(peekThread.preview.mediaType)} ${peekThread.preview.text || tHome("recentMessageAttachment")}`,
          thumbUrl: peekThread.preview.mediaThumbUrl ?? null,
          sender: {
            // Kind must reflect the LAST sender: `PeekSender` resolves the AI
            // persona name for kind "ai"/"bot" and IGNORES `name`, so hardcoding
            // "ai" showed "Lucy" even on an operator's message. Operator → "ai"
            // otherwise (ai/bot/system all read as the assistant identity).
            kind: peekThread.lastSenderKind === "operator" ? "operator" : "ai",
            // Operator's name when they sent the last message (used only for the
            // "operator" kind above); the AI persona is resolved inside PeekSender.
            name:
              peekThread.lastSenderKind === "operator" && peekThread.lastSenderName
                ? peekThread.lastSenderName
                : (peekThread.assignedOperatorName ?? peekThread.senderName),
            ...(peekThread.senderAvatar ? { avatar: peekThread.senderAvatar } : {}),
          },
          // Quick-reply chips off the previewed (last) message, so the peek
          // lets the visitor answer inline. Empty for a plain-text last message.
          buttons: peekThread.preview.buttons ?? [],
        }
      : null;

  // The proactive welcome now rides the SYNTHESISED default thread (see the
  // `threads` memo) — it's a real row in the list, so the launcher peek + badge
  // + resume all flow through the normal path above, and reading it (→
  // `welcomeRead` zeroes the row's unread) clears them like any other thread.
  // Content-stable: `launcherPeek` is rebuilt every render, and the closed-iframe measure effect
  // lists the peek as a dependency, so an event that changed nothing re-ran its rAF settle loop.
  // The launcher message of a MESSAGE slot (015): the top-ranked matching slot while it is unread. A
  // real unread thread's peek wins over it.
  const matchContext = useMatchContextFor(sessionContent.visitors);
  const demoReadStorage = useMemo(() => createMemoryStorage(), []);
  const launcherMessage = useLauncherMessage({
    slots: messageSlots ?? NO_MESSAGE_SLOTS,
    ctx: matchContext,
    publicKey: instance.publicKey,
    // Preview / demo: the read state lives in memory only (a dismissed bubble must not outlive the preview).
    storage: sessionDummy ? demoReadStorage : instance.storage,
    ready: slotLauncher && (sessionDummy || (sessionContent.settled && threadsResolved)),
    isOpen,
    suppressed: launcherPeek !== null,
    previewVisible: env.launcherMessagePreview === "visible",
    blocked: visitorBlocked,
  });
  // The unread launcher message counts on the badge (+1 beside real unread messages). A seeded slot thread
  // has its seed READ on the BE, so it never double-counts.
  const badgeUnread = totalUnread + (launcherMessage.unread ? 1 : 0);
  const slotPeek = useMemo(
    () => (launcherMessage.shown ? slotPeekMessage(launcherMessage.shown, threadAiName) : null),
    [launcherMessage.shown, threadAiName],
  );
  const peekIsSlot = launcherPeek === null && slotPeek !== null;
  // A blocked visitor gets no launcher peek at all (thread or slot): the composer is locked for them.
  const stablePeek = useStableValue(visitorBlocked ? null : (launcherPeek ?? slotPeek));
  const effectivePeek = stablePeek;
  const effectivePeekKey = peekKey;

  // ─── Resume-to-latest-thread ────────────────────────
  // The FIRST Messages open after a (re)load lands on `messages-thread` in the
  // `pendingLatest` state (conversation skeleton, no list flash — see the
  // initial route + `messagesEntryRoute`). Once `visitorThreads` resolves we
  // swap in the actual latest thread, or fall back to the LIST when the visitor
  // has no thread yet. Every in-session Messages open after that lands on the
  // list. The ref lives for the mount, so it resets on reload (fresh mount).
  const didResumeMessagesRef = useRef(false);
  const messagesEntryRoute = useCallback((): WidgetRoute => {
    if (!didResumeMessagesRef.current) return { kind: "messages-thread", pendingLatest: true };
    // In-session Messages open: land in the default (main) thread rather than the
    // list when there's no REAL thread yet — either nothing at all, or only the
    // synthesised default thread (chatId ""). Show the list only once an actual
    // BE thread exists.
    const onlyDefault = threads.length === 1 && threads[0].chatId === "";
    if (threads.length === 0 || onlyDefault) return { kind: "messages-thread", threadKey: null };
    return { kind: "messages" };
  }, [threads]);
  useEffect(() => {
    if (route.kind !== "messages-thread" || !route.pendingLatest) return;
    if (!threadsActive) return;
    // Wait for the first fetch to resolve so we open the ACTUAL latest thread.
    if (threadsLoading) return;
    didResumeMessagesRef.current = true;
    const latest = threads[0];
    setRoute(
      latest
        ? {
            kind: "messages-thread",
            chatId: latest.chatId || undefined,
            threadKey: latest.threadKey,
          }
        : slotLauncher
          ? // MESSAGE slots: the list, whose empty state offers the page's slot (never a new thread).
            { kind: "messages" }
          : // No thread yet → land the visitor IN the default (main) thread (welcome
            // + composer), never the empty list. A brand-new visitor should be able
            // to just start typing; the welcome MESSAGE (if any) shows at the top.
            { kind: "messages-thread", threadKey: null },
    );
  }, [route, threads, threadsLoading, threadsActive, slotLauncher]);
  // The one list row of the page's launcher slot, offered while the visitor has no thread.
  const slotListThread = useMemo(
    () => (slotLauncher ? slotListRow(launcherMessage.top, localSlots.keyFor, threadAiName) : null),
    [slotLauncher, launcherMessage.top, localSlots.keyFor, threadAiName],
  );
  // Mark the default thread read the moment the visitor is actually looking at
  // the (main) thread — via the peek, the entry route, or "ask a question".
  // Covers every entry path so its peek + badge clear once and stay cleared.
  useEffect(() => {
    if (welcomeRead || !defaultThreadText) return;
    if (isOpen && route.kind === "messages-thread" && !route.threadKey) markWelcomeRead();
  }, [isOpen, route, defaultThreadText, welcomeRead, markWelcomeRead]);
  // A send in a slot thread of a preview without a BE creates that thread (in memory) for the list.
  const handleTabOutbound = () => {
    playOutbound();
    if (!sessionDummy || !slotLauncher || route.kind !== "messages-thread") return;
    const slotId = slotIdOfThreadKey(route.threadKey);
    if (!slotId || demoSlotThreads[slotId]) return;
    const slot = messageSlots?.find((s) => s.kind === "MESSAGE" && s.id === slotId);
    const variant = slot ? pickVariant(slot.variants, matchContext) : null;
    if (!variant) return;
    setDemoSlotThreads((prev) => ({
      ...prev,
      [slotId]: { title: variant.text, at: new Date().toISOString() },
    }));
  };
  const markMessagesResumed = useCallback(() => {
    didResumeMessagesRef.current = true;
  }, []);
  // Where the visitor last was inside Messages (the list or a thread), in memory for this panel session.
  // Re-entering the tab restores it; the first entry uses the resolver.
  const rememberedMessagesRef = useRef<RememberedMessagesRoute | null>(null);
  const topSlotIdRef = useRef<string | null>(null);
  // With variant threads the page's thread is slot + variant, so a variant change drops the restore too.
  topSlotIdRef.current = launcherMessage.top
    ? variantThreads
      ? `${launcherMessage.top.slotId}:${launcherMessage.top.variant.id}`
      : launcherMessage.top.slotId
    : null;
  // Stamped on a ROUTE change only (the page's slot is read from its ref at that moment).
  useEffect(() => {
    if (isRememberableMessagesRoute(route)) {
      rememberedMessagesRef.current = { route, topSlotId: topSlotIdRef.current };
    }
  }, [route]);
  const resumeRoute = useCallback(
    () => resumeMessagesRoute(rememberedMessagesRef.current, topSlotIdRef.current),
    [],
  );
  const launcherActions = useLauncherActions({
    resumeRoute,
    message: launcherMessage,
    slotLauncher,
    launcherToMessages: pickInitialTab(config.defaultTab, config.features) === "messages",
    threads,
    threadsLoading: threadsActive && threadsLoading,
    isOpen,
    setRoute,
    markResumed: markMessagesResumed,
    openPanel,
    togglePanel,
    setPendingReply: setPendingSlotReply,
    keyFor: localSlots.keyFor,
  });

  // Seed the operator-handoff status for the OPEN thread; realtime `assignment`
  // events keep it current after that. network-only so a re-open doesn't paint
  // a stale cached status. Keyed on the open conversation's chatId (per-thread
  // banner) — self-corrects PENDING/CONNECTED on every open + thread switch.
  // The conversation on screen, resolved like the per-chat stream's: the chatless anchor thread is the
  // session chat (`|| adoptedChatId` for a drive-by's lazily-created one).
  const streamChatId = openConversationChatId({
    isOpen,
    route,
    anchorChatId: session.chatId || adoptedChatId,
  });
  const assignmentQuery = useVisitorChatAssignmentQuery({
    variables: { chatId: streamChatId ?? "" },
    // Only while a conversation is actually open (the banner renders only there)
    // — skipped on the list / a not-yet-created new thread / closed widget.
    skip: sessionDummy || !streamChatId || !session.token,
    fetchPolicy: "network-only",
  });
  useEffect(() => {
    // A live SSE `assignment` event already took over — this in-flight
    // `network-only` query can resolve AFTER that event lands (it's racing
    // from mount), and its snapshot would otherwise clobber the fresher
    // live status for the rest of the session (this effect only fires once
    // per chat). Bail out rather than overwrite.
    if (assignmentTouchedBySseRef.current) return;
    const next = assignmentFromQuery(assignmentQuery.data?.visitorChatAssignment);
    if (next) setAssignment(next);
  }, [assignmentQuery.data]);

  // Re-fetch the visitor profile whenever the underlying identity
  // changes — anon → identified after a passkey / Google sign-in, or a
  // different identified person on a shared device. Without this the
  // ProfileTab + `useVisitorProfilePreferences` would keep showing the
  // PRIOR session's cached profile (the `VisitorProfile` query takes no
  // variables, so the cache key doesn't move when the auth header does;
  // Apollo happily returns the anon result to an identified session).
  // Keyed off `session.peopleId` since that's the BE-resolved row id —
  // stable per user, flips on every real identity change. Skipped while
  // the feature is off or there's no session yet.
  // biome-ignore lint/correctness/useExhaustiveDependencies: peopleId is the deliberate invalidation trigger; apolloClient is a stable instance
  useEffect(() => {
    if (!config.features.profile) return;
    if (!session.peopleId || !session.token) return;
    // Invalidate the cached profile (it's keyed by no variables, so it doesn't
    // move when the auth identity does). EVICT rather than `refetchQueries`:
    // the ProfileTab query is lazy/unmounted most of the time, and
    // `refetchQueries({ include: [...] })` warns "Unknown query named
    // VisitorProfile" when nothing's actively watching it. Eviction refreshes
    // an active watcher AND makes the next ProfileTab mount fetch fresh, with
    // no warning.
    apolloClient.cache.evict({ id: "ROOT_QUERY", fieldName: "visitorProfile" });
    apolloClient.cache.gc();
  }, [session.peopleId]);

  /**
   * Append a message to the in-session live list. Dedupes by id so
   * SSE replays + optimistic-then-confirmed sends don't double up.
   */
  const appendLiveMessage = useCallback((msg: MockMessage) => {
    setLiveMessages((prev) => {
      if (prev.some((m) => m.id === msg.id)) return prev;
      return [...prev, msg];
    });
  }, []);

  /** Apply a realtime edit (`messageUpdate`) to the live buffer, in place by id. */
  const updateLiveMessage = useCallback((msg: MockMessage) => {
    setLiveMessages((prev) => applyMessageUpdate(prev, msg));
  }, []);

  /** Apply a realtime delete (`messageDelete`): hide the bubble, live or from history. */
  const deleteLiveMessage = useCallback((id: string) => {
    setLiveMessages((prev) => applyMessageDelete(prev, id));
  }, []);

  /** Home `recent-message` block: write the announced inbound straight into the Apollo cache. */
  const writeRecentInbound = (e: ThreadUpdateEvent) => {
    // The block shows the anchor chat's latest inbound only.
    if (!session.chatId || e.threadId !== session.chatId) return;
    apolloClient.writeQuery({
      query: VisitorChatRecentInboundDocument,
      variables: { chatId: session.chatId },
      data: { __typename: "Query", visitorChatRecentInbound: recentInboundFromFrame(e) },
    });
  };

  /**
   * After a gap on the per-chat stream: refetch the open conversation's messages (merged with the live
   * buffer by id) and its assignment state.
   */
  const resyncOpenConversation = () => {
    void apolloClient.refetchQueries({ include: [VisitorChatHistoryDocument] });
    if (!streamChatId || sessionDummy) return;
    void resyncAssignment({
      refetch: async () =>
        assignmentFromQuery((await assignmentQuery.refetch()).data?.visitorChatAssignment),
      apply: setAssignment,
      touchedRef: assignmentTouchedBySseRef,
    });
  };

  /**
   * Swap an optimistic tmp-id row for the BE-persisted real row.
   * Used by the visitor's send path — append optimistic with a
   * `tmp-…` id, fire the mutation, replace once the BE returns the
   * canonical message id. Keeps the bubble visible the whole time
   * AND lets history-cache dedupe work on later reads.
   */
  const replaceLiveMessage = useCallback((tmpId: string, msg: MockMessage) => {
    // Preserve the original row's `clientKey` across the swap so
    // React's reconciler treats it as the SAME bubble. Without this
    // the AnimatePresence key flips when `id` changes from tmp-… to
    // the BE-canonical id, the bubble unmounts + remounts, and the
    // visitor sees their just-sent message "re-animate" the moment
    // an SSE event triggers a re-render. The caller can still pass
    // an explicit `clientKey` to override (rare).
    setLiveMessages((prev) =>
      prev.map((m) =>
        m.id === tmpId ? { ...msg, clientKey: msg.clientKey ?? m.clientKey ?? tmpId } : m,
      ),
    );
  }, []);

  /**
   * Drop an optimistic row entirely (keyed by its outbox `clientKey`). Used
   * when the outbox gives up on a send it can never deliver — a PERMANENT BE
   * rejection (e.g. text over the char cap): the op is removed from the queue
   * and the bubble is removed here, so nothing lingers as "not delivered".
   */
  const removeLiveMessage = useCallback((clientKey: string) => {
    setLiveMessages((prev) => prev.filter((m) => (m.clientKey ?? m.id) !== clientKey));
  }, []);
  // Stable register fn — MessagesTab calls this on mount with its
  // handler; the returned cleanup nulls the ref on unmount. Stable
  // identity is critical: a fresh function each render would loop
  // the tab's `useEffect`.
  const registerMessagesRealtimeHandler = useRef((handler: (e: RealtimeMessageEvent) => void) => {
    messagesTabHandlerRef.current = handler;
    return () => {
      if (messagesTabHandlerRef.current === handler) {
        messagesTabHandlerRef.current = null;
      }
    };
  }).current;

  // ONE per-chat stream: the open conversation's, only while the panel is open on it. Everything
  // widget-wide (badge, peek, sound, notification, Home block) rides the threads stream above.
  useOpenConversationStream({
    chatId: streamChatId,
    token: session.token,
    visitorId: session.peopleId,
    enabled: !sessionDummy && mode === "production",
    aiName: threadAiName,
    handlerRef: messagesTabHandlerRef,
    sinks: {
      setAssignment: (next) => {
        setAssignment(next);
        // Mark the live status authoritative so the seed query's in-flight snapshot cannot overwrite it.
        assignmentTouchedBySseRef.current = true;
      },
      appendMessage: appendLiveMessage,
      updateMessage: updateLiveMessage,
      deleteMessage: deleteLiveMessage,
      setContactBlock: setLiveContactBlock,
    },
    resync: resyncOpenConversation,
  });

  // Quick-reply sender used by the launcher peek. Real mode goes
  // through the existing `sendVisitorMessage` mutation; demo mode
  // is handled inline (no BE) — we just open the panel so the
  // messages tab takes over.
  const [sendVisitor] = useSendVisitorMessage();
  // Media upload for the queue's drain — a media message attached offline
  // carries raw bytes; `run` uploads them here before sending.
  const [uploadVisitorMedia] = useUploadVisitorMediaMutation();
  // Re-hosts a picked Giphy asset into our media store at drain time (so the
  // optimistic bubble shows instantly from the preview while this runs).
  const giphyIngest = useGiphyIngest();
  // Profile mutation for convergence ops (preferences / profile) driven off the
  // queue — the UI already applied the change optimistically; this is the
  // background server sync, retried on reconnect.
  const [updateVisitorProfile] = useUpdateVisitorProfileMutation();
  const [submitReaction] = useSubmitVisitorReactionMutation();
  const [markVisitorChatRead] = useMarkVisitorChatReadMutation();
  // Tracking mutations — a page-view / link-click queued while the BE is
  // unreachable persists (IndexedDB) and retries here until delivered; each
  // event carries its own `occurredAt`, so a late send is recorded correctly.
  const [trackPageView] = useTrackVisitorPageViewMutation();
  const [trackLinkClick] = useTrackVisitorLinkClickMutation();
  const [trackButtonClick] = useTrackVisitorButtonClickMutation();

  // Object-URL cache for offline-attached media previews. A pending item has
  // only `dataBase64` (no S3 URL until it uploads), so the optimistic bubble
  // renders a blob: URL made from those bytes — created ONCE per (clientKey,
  // index) and revoked when the op leaves the queue (see the cleanup effect).
  const mediaUrlCacheRef = useRef<Map<string, string>>(new Map());
  const outboxMediaPreviews = useCallback(
    (clientKey: string, items: OutboxMediaItem[] | undefined): MockMessageMedia[] => {
      if (!items || items.length === 0) return [];
      return items.map((it, i) => {
        // s3Url once re-hosted; else the Giphy preview for an instant bubble;
        // else an object URL for offline-attached bytes.
        let url = it.s3Url ?? it.previewUrl;
        if (!url && it.dataBase64) {
          const key = `${clientKey}:${i}`;
          url = mediaUrlCacheRef.current.get(key);
          if (!url) {
            url = URL.createObjectURL(base64ToBlob(it.dataBase64, it.mimetype));
            mediaUrlCacheRef.current.set(key, url);
          }
        }
        return {
          url: url ?? "",
          mimetype: it.mimetype,
          alt: it.filename,
          // Giphy preview dimensions reserve the bubble's aspect-ratio box up
          // front (the ingested/persisted row later carries the BE Media dims).
          ...(it.previewWidth && it.previewHeight
            ? { width: it.previewWidth, height: it.previewHeight }
            : {}),
        };
      });
    },
    [],
  );

  // ─── Offline operation queue ("outbox") ─────────────────────────────────
  // A generic, persistent queue for everything the widget sends on the
  // visitor's behalf: it persists ops in IndexedDB (survive reload), retries on
  // reconnect, and (for messages) drives the optimistic bubble's status. The
  // hook is op-agnostic — `run` dispatches each op to its BE call by `op.op`,
  // and `onSent` reconciles per op. Phase 1/2 wire "sendMessage"; profile /
  // preferences / reactions / read-acks / tracking are added as new op kinds on
  // the SAME queue (convergence / beacon semantics — see `outbox-store`).
  const [droppedOp, setDroppedOp] = useState<{ op: string; at: number } | null>(null);
  const outboxPublicKey = instance.outboxKey;
  // The queue runs for a real production visitor with a scope key — enabled as
  // soon as we have the `publicKey`, NOT gated on the auth token. This is what
  // makes offline persistence actually resilient: a page-view / message queued
  // while the BE (and thus `StartAnonymousVisitor`) is unreachable still
  // persists to IndexedDB and retries until the visitor is authed AND the BE is
  // up — `run`'s mutations pick up the JWT live once the handshake lands. When
  // it's off (dummy / non-production), the tab falls back to its direct send.
  const outboxEnabled = !sessionDummy && mode === "production" && !!outboxPublicKey;
  const outbox = useOutbox({
    publicKey: outboxPublicKey,
    enabled: outboxEnabled,
    isolated: instance.isolated,
    // A rejected preview token holds the queue: ops stay queued and drain on the new token.
    paused: preview.paused,
    // A 400 `BAD_USER_INPUT` (e.g. text over the BE's char cap) can only re-fail
    // on retry, so the queue DROPS it rather than hammer the BE / leave a stuck
    // "not delivered" bubble. Transient errors (offline, 5xx) keep retrying.
    isPermanent: isPermanentBackendError,
    onDropped: (op, error) => {
      // The dropped op's optimistic bubble can never deliver — remove it.
      if (op.op === "sendMessage") removeLiveMessage(op.clientKey);
      // An older BE refuses messageSlot on a REGULAR thread's first send: retry once without it.
      const retry = regularThreadMismatchRetry({ op, error });
      if (retry) {
        outbox.enqueue({ op: "sendMessage", semantics: "queue", payload: retry });
        return;
      }
      // The BE does not know the slot (an unsaved preview, props, or deleted meanwhile): it is a local
      // slot from now on. Re-send the text ONCE into its local thread (no `messageSlot`), keep the
      // visitor in that thread with the header: nothing goes to the main chat, nothing is lost.
      const fallback = slotNotFoundFallback({ op, error });
      if (fallback) {
        const slot = messageSlots?.find((sl) => sl.kind === "MESSAGE" && sl.id === fallback.slotId);
        const sent = (op.payload as { messageSlot?: { variantId?: string } }).messageSlot;
        localSlots.markLocal(
          fallback.slotId,
          slot?.variants.find((v) => v.id === sent?.variantId),
          !!variantIdOfThreadKey(fallback.failedThreadKey),
        );
        outbox.enqueue({ op: "sendMessage", semantics: "queue", payload: fallback.payload });
        setRoute((r) =>
          r.kind === "messages-thread" && r.threadKey === fallback.failedThreadKey
            ? {
                kind: "messages-thread",
                threadKey: fallback.payload.threadKey,
                // In place: the same layer and tab stay mounted, so the welcome never shows twice.
                keyAs: r.keyAs ?? fallback.failedThreadKey,
              }
            : r,
        );
      }
      // A rejected profile / preference write was already applied optimistically: roll it back to
      // what the server holds and let the profile tab say so.
      if (isProfileOp(op.op)) {
        discardOptimisticProfile(apolloClient);
        if (op.op === "setPreferences") void restoreServerLanguage(apolloClient);
        setDroppedOp({ op: op.op, at: Date.now() });
      }
    },
    run: async (op, ctx) => {
      if (op.op === "sendMessage") {
        const p = op.payload as OutboxMessagePayload;
        let media = p.media;
        // Upload any PENDING media first (bytes attached offline). Persist the
        // resolved mediaId back via `ctx.patch` so a send-failure retry doesn't
        // re-upload (avoids orphan uploads on the BE).
        if (media?.some((m) => !m.mediaId && (m.dataBase64 || m.giphyUrl))) {
          const resolved: OutboxMediaItem[] = [];
          for (const m of media) {
            if (m.mediaId) {
              resolved.push(m);
              continue;
            }
            // A picked Giphy asset — re-host it from its source URL into our
            // media store (the picker showed the preview optimistically).
            if (m.giphyUrl) {
              const out = await giphyIngest(m.giphyUrl, m.gifKind ?? "IMAGE");
              resolved.push({
                filename: m.filename,
                mimetype: out.mimetype || m.mimetype,
                mediaId: out.mediaId,
                s3Url: out.url ?? m.s3Url,
              });
              continue;
            }
            if (!m.dataBase64) {
              resolved.push(m);
              continue;
            }
            const up = await uploadVisitorMedia({
              variables: {
                input: { filename: m.filename, mimetype: m.mimetype, dataBase64: m.dataBase64 },
              },
            });
            const out = up.data?.uploadVisitorMedia;
            if (!out?.mediaId) throw new Error("media upload failed");
            resolved.push({
              filename: m.filename,
              mimetype: m.mimetype,
              mediaId: out.mediaId,
              s3Url: out.url ?? m.s3Url,
            });
          }
          media = resolved;
          ctx.patch({ ...p, media });
        }
        const mediaIds = (media ?? []).map((m) => m.mediaId).filter((id): id is string => !!id);
        // No chatId in the input — the BE resolves the target chat from the
        // visitor JWT + threadKey (a fresh threadKey deterministically creates /
        // upserts the topic thread; see the backend's externalChatId scheme).
        const res = await sendVisitor({
          variables: {
            input: {
              ...(p.text ? { text: p.text } : {}),
              ...(p.buttonValue !== undefined ? { buttonValue: p.buttonValue } : {}),
              ...(mediaIds.length > 0 ? { mediaIds } : {}),
              ...(p.threadKey ? { threadKey: p.threadKey } : {}),
              ...(p.messageSlot ? { messageSlot: p.messageSlot } : {}),
              ...(p.page ? { page: p.page } : {}),
            },
          },
        });
        // A GraphQL rejection (e.g. 400 text-too-long) rides in `res.errors` —
        // rethrow so the outbox can classify + drop it, not treat the null ack
        // as a transient failure and retry forever.
        throwOnGraphQLErrors(res);
        const ack = res.data?.sendVisitorMessage;
        if (!ack) throw new Error("sendMessage: empty ack");
        return { messageId: ack.messageId, chatId: ack.chatId };
      }
      if (op.op === "setPreferences") {
        // Convergence op: the payload is the FULL current preferences patch
        // (the leaf already wrote it to the Apollo cache optimistically). We
        // just sync it to the BE; retries are idempotent (last-write-wins).
        throwOnGraphQLErrors(
          await updateVisitorProfile({
            variables: { input: { preferences: op.payload as Record<string, unknown> } },
          }),
        );
        return {};
      }
      if (op.op === "reaction") {
        // Convergence op (per surface+item): the bar already applied the vote to
        // the Apollo cache optimistically. Sync the latest pick; a rapid re-tap
        // superseded any earlier op for the same item, so this IS the last pick.
        throwOnGraphQLErrors(
          await submitReaction({
            variables: {
              input: op.payload as { surface: ReactionSurface; itemId: string; slotIndex: number },
            },
          }),
        );
        return {};
      }
      if (op.op === "setProfile") {
        // Convergence op (profile fields): the form applied the edit locally +
        // showed "saved". Sync it; a later save supersedes (its diff already
        // includes any still-unsynced fields).
        throwOnGraphQLErrors(
          await updateVisitorProfile({
            variables: { input: op.payload as Record<string, unknown> },
          }),
        );
        return {};
      }
      if (op.op === "readAck") {
        // Convergence op (per chat): the visitor read the thread — the badge/
        // unread already cleared locally. Sync `markVisitorChatRead`; a later
        // read on the same chat supersedes this one.
        const { chatId } = op.payload as { chatId: string };
        throwOnGraphQLErrors(await markVisitorChatRead({ variables: { chatId } }));
        return {};
      }
      if (op.op === "trackPageView") {
        // Telemetry op (page / link / button events): a persisted `queue` op, retried until
        // delivered; each event carries its own `occurredAt`, so a late send is recorded correctly.
        await trackPageView({ variables: { input: op.payload as TrackVisitorPageViewInput } });
        return {};
      }
      if (op.op === "trackLinkClick") {
        await trackLinkClick({ variables: { input: op.payload as TrackVisitorLinkClickInput } });
        return {};
      }
      if (op.op === "trackButtonClick") {
        await trackButtonClick({
          variables: { input: op.payload as TrackVisitorButtonClickInput },
        });
        return {};
      }
      throw new Error(`outbox: no handler for op "${op.op}"`);
    },
    onSent: (op, ack) => {
      // A profile write that landed offline-first changes `contact.*`: refetch the session content.
      if (isProfileOp(op.op)) sessionContent.refresh();
      if (op.op !== "sendMessage") return;
      const p = op.payload as OutboxMessagePayload;
      const preview = outboxMediaPreviews(op.clientKey, p.media);
      // Swap the optimistic bubble (keyed by clientKey) for the BE-canonical
      // row so a later history read dedupes against it.
      if (ack.messageId) {
        replaceLiveMessage(op.clientKey, {
          id: ack.messageId,
          clientKey: op.clientKey,
          direction: "INBOUND",
          text: p.text,
          ageLabel: new Date().toLocaleTimeString(undefined, {
            hour: "numeric",
            minute: "2-digit",
          }),
          sender: { kind: "visitor", name: "" },
          ...(preview.length > 0 ? { media: preview } : {}),
          deliveryStatus: "DELIVERED",
        });
      }
      // A new-question thread's first send just created its chat — adopt the
      // real chatId, but ONLY into the still-open, still-chatId-less thread with
      // the SAME threadKey (don't hijack the route if the visitor moved on).
      if (ack.chatId && p.chatId == null) {
        setRoute((r) =>
          r.kind === "messages-thread" && !r.chatId && r.threadKey === p.threadKey
            ? { ...r, chatId: ack.chatId as string }
            : r,
        );
        refetchThreads();
        setThreadsEpoch((e) => e + 1);
      }
    },
  });

  // Message-send convenience over the generic `enqueue` — the tab hands over a
  // message payload; we wrap it as a "sendMessage" queue op.
  const enqueueSend = useCallback(
    (payload: OutboxMessagePayload) => {
      // The page is captured NOW (the send), not when a queued op replays later.
      const page = payload.page ?? captureVisitorPage(env.kind);
      outbox.enqueue({
        op: "sendMessage",
        semantics: "queue",
        payload: page ? { ...payload, page } : payload,
      });
    },
    [outbox.enqueue, env.kind],
  );

  clearOutboxRef.current = outbox.clear;
  // Exposed to the whole widget tree (OutboxProvider) so any leaf — profile
  // prefs, reactions, tracking — can queue an op without prop-drilling. Only
  // live when the queue is enabled; otherwise leaves fall back to their own
  // direct mutation (the provider value's `enqueue` is still callable but the
  // underlying store is inert when disabled).
  const outboxContextValue = useMemo(
    () => ({ enqueue: outbox.enqueue, online, dropped: droppedOp }),
    [outbox.enqueue, online, droppedOp],
  );

  // Reconcile the MESSAGE ops into the live message list: every queued message
  // gets an optimistic bubble (keyed by `clientKey`) whose deliveryStatus
  // mirrors its queue state. This is also the REHYDRATE path — messages
  // restored from IndexedDB on a fresh load reappear as bubbles here. A sent op
  // is dropped from the queue (its DELIVERED bubble was already swapped in by
  // `onSent`), so this loop never touches it again. Non-message ops (profile /
  // reactions / …) are ignored here — they have no bubble. Only the ops of the
  // OPEN conversation are shown (the buffer is shared across threads); re-run on
  // a thread switch so a thread's still-queued sends reappear after the reset.
  useEffect(() => {
    setLiveMessages((prev) =>
      reconcileOutboxOps(prev, outbox.ops, activeConvId, outboxMediaPreviews),
    );
  }, [outbox.ops, outboxMediaPreviews, activeConvId]);

  // Revoke cached preview blob: URLs once their op leaves the queue (sent /
  // discarded), so offline-attached media bytes don't pin in memory forever.
  useEffect(() => {
    const alive = new Set(outbox.ops.map((o) => o.clientKey));
    const cache = mediaUrlCacheRef.current;
    for (const [key, url] of cache) {
      if (!alive.has(key.split(":")[0])) {
        URL.revokeObjectURL(url);
        cache.delete(key);
      }
    }
  }, [outbox.ops]);

  // Prefetch the PUBLIC list surfaces (news + help collections/root) into the
  // offline cache the first time the visitor opens the widget while online, so
  // those tabs still render if the BE later goes down — even if the visitor
  // never opened them (config caches on boot; these are otherwise lazy, cached
  // only on tab visit). Article DETAILS stay cache-on-visit (can't prefetch
  // every one). Runs once per locale; only for enabled tabs.
  const prefetchedLocaleRef = useRef<string | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: apolloClient is a stable instance; config.features read at fire time
  useEffect(() => {
    if (sessionDummy || mode !== "production" || !online || !isOpen || !session.token) return;
    const pk = instance.publicKey;
    if (!pk) return;
    if (prefetchedLocaleRef.current === locale) return;
    prefetchedLocaleRef.current = locale;
    const warm = async <T,>(key: string, query: Parameters<typeof apolloClient.query>[0]) => {
      try {
        const res = await apolloClient.query({ ...query, fetchPolicy: "network-only" });
        if (res.data) void cacheSet<T>(key, res.data as T);
      } catch {
        // Prefetch is best-effort — a miss just means no offline copy yet.
      }
    };
    if (config.features.news) {
      void warm(cacheKeys.news(pk, locale), {
        query: VisitorNewsListDocument,
        variables: { args: { locale, limit: 10, offset: 0 } },
      });
    }
    if (config.features.help) {
      void warm(cacheKeys.helpCategories(pk, locale), {
        query: VisitorHelpFoldersDocument,
        variables: { parentId: null, locale },
      });
      void warm(cacheKeys.helpRoot(pk, locale), {
        query: VisitorHelpListDocument,
        variables: { args: { folderId: null, locale, recursive: false, limit: 20 } },
      });
    }
  }, [isOpen, online, session.token, locale, sessionDummy, mode, apolloClient]);

  // Warm the LAZY article-view chunks while online, so opening an article works
  // offline too. The views are `lazy()` (they carry the ~80 KB markdown stack —
  // kept out of the initial payload); a fresh open with the BE/network down
  // otherwise fails with a ChunkLoadError because the chunk can't be fetched.
  // Preloading here lets the browser cache the chunk (served from disk cache on
  // a later offline load) and keeps it resident for the current session.
  const chunksWarmedRef = useRef(false);
  useEffect(() => {
    if (chunksWarmedRef.current || isDummy || !online || !isOpen) return;
    chunksWarmedRef.current = true;
    void import("../help-article-view");
    void import("../news-article-view");
  }, [online, isOpen, isDummy]);

  // Visitor-triggered resolve for an operator-connected chat — hands
  // the conversation back to the AI.
  const [resolveChat] = useResolveVisitorChatMutation();
  const handleResolve = useCallback(() => {
    if (!session.chatId) return;
    // Optimistic: hide the button + pending line immediately. The
    // CHAT_RESOLVED system row arrives via the assignment:null SSE echo
    // (with the real messageId), so we do NOT synthesize it here.
    const prev = assignment;
    setAssignment(EMPTY_ASSIGNMENT);
    resolveChat({ variables: { chatId: session.chatId } }).catch(() => {
      // Restore so the visitor can retry — the handoff is still active on
      // the BE; only our optimistic local state needs to roll back.
      setAssignment(prev);
    });
  }, [resolveChat, session.chatId, assignment]);

  // ─── Download transcript ────────────────────────────────────────────────
  // Fetches the FULL conversation (unpaginated `visitorChatTranscript`), maps
  // it with the SAME history mapper the thread uses, formats a plain-text .txt
  // client-side (widget owns the wording / visitor locale + timezone), and
  // triggers a download. A blocked download (sandboxed iframe) falls back to
  // opening the text in a new tab.
  const [transcriptDownloading, setTranscriptDownloading] = useState(false);
  const handleDownloadTranscript = useCallback(
    async (chatId: string) => {
      if (!chatId || transcriptDownloading) return;
      setTranscriptDownloading(true);
      try {
        const res = await apolloClient.query({
          query: VisitorChatTranscriptDocument,
          variables: { chatId },
          fetchPolicy: "network-only",
        });
        throwOnGraphQLErrors(res);
        const raw = (res.data as { visitorChatTranscript?: unknown } | null)?.visitorChatTranscript;
        // `visitorChatTranscript` shares the history item shape (`{ items }`), so
        // reuse the mapper — but the transcript is already chronological
        // (oldest→newest) whereas the mapper REVERSES (history is newest-first).
        // Flip the result back so the transcript reads oldest→newest.
        const aiName = resolveAiName(config.messenger?.aiAssistant) ?? AI_ASSISTANT_NAME;
        const messages = mapHistoryToMessages({ visitorChatHistory: raw }, locale, aiName)
          .slice()
          .reverse();
        const truncated =
          !!raw && typeof raw === "object" && (raw as { truncated?: unknown }).truncated === true;
        const orgName = config.messenger?.title?.trim() || AI_ASSISTANT_NAME;
        const visitorLabel =
          visitorIdentity?.email?.trim() || visitorIdentity?.name?.trim() || tTranscript("you");
        const text = buildTranscript(messages, {
          orgName,
          visitorLabel,
          aiName,
          locale,
          truncated,
          t: tTranscript,
        });
        // Filename: {org}-{chatId}-{YYYY_MM_DD_HHmmssSSS}.txt
        const stem = safeFilenameStem(orgName, "conversation");
        const d = new Date();
        const p2 = (n: number) => String(n).padStart(2, "0");
        const stamp =
          `${d.getFullYear()}_${p2(d.getMonth() + 1)}_${p2(d.getDate())}_` +
          `${p2(d.getHours())}${p2(d.getMinutes())}${p2(d.getSeconds())}` +
          `${String(d.getMilliseconds()).padStart(3, "0")}`;
        const ok = downloadTextFile(`${stem}-${chatId}-${stamp}.txt`, text);
        if (!ok) {
          // Deliberately NOT lib/safe-open.ts `openExternalUrl`: that is the choke point for links taken from
          // BE / operator / host data and its allowlist rejects `data:`. This URL is built here from the
          // visitor's own transcript text (no external input), as the fallback when the download cannot start.
          window.open(
            `data:text/plain;charset=utf-8,${encodeURIComponent(text)}`,
            "_blank",
            "noopener,noreferrer",
          );
        }
      } catch (err) {
        console.warn("[wexio] transcript download failed:", err);
      } finally {
        setTranscriptDownloading(false);
      }
    },
    [
      transcriptDownloading,
      apolloClient,
      locale,
      config.messenger?.title,
      config.messenger?.aiAssistant,
      visitorIdentity,
      tTranscript,
    ],
  );

  // Start a fresh conversation — mints a new per-topic key and drills into an
  // empty thread (the anchor/main thread stays key-less). Shared by the thread
  // list's "new question" action and the CLOSED-conversation "start new" button.
  const handleStartNewConversation = useCallback(() => {
    const newKey =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `thread-${Date.now()}`;
    // With message slots the new conversation begins with the page's slot text (015).
    drillTo(launcherActions.newConversationRoute(newKey));
  }, [drillTo, launcherActions]);

  // Routes that can widen — visitor benefits from more reading room
  // when they're inside a conversation or a single article. List
  // surfaces (home / help index / news index) stay at the default
  // width so the chrome composition the visitor expects holds.
  // **Persistence**: `expanded` is stored in localStorage
  // (`PANEL_EXPANDED_KEY`), so it survives navigation, reloads, AND
  // revisits — expand on Messages, leave, come back later, still wide.
  // Non-expandable routes simply ignore the flag via `effectiveExpanded`
  // rather than resetting it.
  // The thread LIST (`messages`) stays at the default width — only a single
  // thread conversation and full article reads expand. Otherwise expanding a
  // thread and popping back would leave the list stuck wide too (the flag is
  // shared + persisted).
  const expandable =
    route.kind === "messages-thread" ||
    route.kind === "help-article" ||
    route.kind === "news-article";
  const effectiveExpanded = expandable && expanded;

  // IDEAL dimensions — the widget wants 420 × 670 (640 × 860 expanded).
  // These are what we REPORT to the host (iframe loader / web-component),
  // which clamps to the host viewport itself.
  const idealW = effectiveExpanded ? 640 : 420;
  // Default-state height is 670 (trimmed 50px from 720) for a more
  // compact panel; the expanded state stays tall.
  const idealH = effectiveExpanded ? 860 : 670;

  // Viewport size — tracked so the expand ANIMATION targets the size the
  // panel will actually render at. Without this the spring animates the
  // height toward 860 even when the screen is shorter, so the height
  // meets its `max-h` ceiling and stops while the width is still moving
  // (the "height finishes first" the visitor sees, especially on screens
  // under ~860px tall). `null` until mounted → SSR-safe, no hydration gap
  // (panel is closed at first paint).
  const [viewport, setViewport] = useState<{ w: number; h: number } | null>(null);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const read = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      // Bail when the size didn't actually change — `resize` also fires
      // on zoom / mobile keyboard / devtools docking with no delta.
      // Returning the same reference makes React skip the re-render, so
      // the only renders are the ones that genuinely re-fit the panel.
      setViewport((prev) => (prev && prev.w === w && prev.h === h ? prev : { w, h }));
    };
    read();
    window.addEventListener("resize", read);
    return () => window.removeEventListener("resize", read);
  }, []);

  // CLAMPED dimensions used by the open/expand animation. Mirrors the CSS
  // `max-w / max-h`, so the spring animates to exactly what renders and
  // both axes settle together. Embedded (host-container) keeps the ideal
  // — the CSS `%` clamps handle it and we can't measure the container as
  // cheaply; the viewport path covers iframe + web-component.
  // The iframe embed is excluded too: there `viewport` is the FRAME, which
  // starts launcher-sized and only grows AFTER the open resize round-trips
  // through the host. Deriving the target from it made the panel animate to a
  // tiny size, then re-target mid-spring as the frame grew (a slow rise from
  // the bottom) and settle smaller than the web-component/React panel. The frame
  // is sized FROM the ideal (+ margins) below, so the ideal is the right target;
  // the loader's viewport caps + the CSS max-w/max-h still clamp small screens.
  // The iframe clamps to the HOST viewport instead (the loader reports it): without it the
  // expand spring aimed past the CSS max-h, so the height hit that cap early and the width kept
  // growing ("up, then left"), unlike the web-component/React panel that grows both together.
  const isFrame = env.kind === "iframe";
  const hostViewport = useIframeHostViewport();
  const clampTo = embedded
    ? null
    : isFrame
      ? hostViewport
      : viewport
        ? { width: viewport.w, height: viewport.h }
        : null;
  const panelW = clampTo ? Math.min(idealW, clampTo.width - PANEL_MARGIN_X) : idealW;
  const panelH = clampTo?.height ? Math.min(idealH, clampTo.height - PANEL_MARGIN_Y) : idealH;

  // On a phone the panel is already as wide as the screen allows, so the control would do nothing.
  // The iframe's own width is the frame, so there the loader reports the host width (`null` until it does).
  const iframeHostWidth = hostViewport?.width ?? null;
  const toggleExpand = useMemo(
    () =>
      expandHasRoom(embedded ? null : isFrame ? iframeHostWidth : (viewport?.w ?? null))
        ? () => setExpanded((v) => !v)
        : undefined,
    [embedded, isFrame, iframeHostWidth, viewport?.w],
  );

  // Remember the place (open + route) per tab so a host page load lands where the visitor left.
  // Live production sessions only (preview / demo / isolated sessions never persist); the host's own
  // open / route intent wins; a phone (full-screen panel) never auto-opens.
  const placeWidth = embedded
    ? Number.POSITIVE_INFINITY
    : isFrame
      ? iframeHostWidth
      : (viewport?.w ?? null);
  usePlaceMemory({
    storage: instance.sessionStorage,
    publicKey: instance.publicKey,
    enabled: !configLoading && mode === "production" && !sessionDummy && !instance.isolated,
    features: config.features,
    route,
    setRoute,
    isOpen,
    requestOpen: hostOpen,
    phone: placeWidth === null ? null : !expandHasRoom(placeWidth),
    hostOwnsOpen: env.open !== undefined || env.defaultOpen !== undefined,
    hostOwnsRoute: env.previewTab !== undefined,
  });

  // Track navigation direction so transitions feel like "going
  // deeper" (slide left + scale down a touch) or "going back"
  // (slide right + scale up). Tab indices give us a natural order;
  // drill-ins use a higher rank than their parent tab.
  const lastRankRef = useRef<number>(routeRank(route));
  const direction = (() => {
    const next = routeRank(route);
    const prev = lastRankRef.current;
    lastRankRef.current = next;
    if (next === prev) return 0;
    return next > prev ? 1 : -1;
  })();

  // Resize the embed surface based on open / closed / expanded state.
  // Reports the IDEAL dimensions (NOT the viewport-clamped ones) — the
  // iframe loader's styles + the web-component host clamp to viewport on
  // narrow screens. Reporting ideal here also breaks a feedback loop:
  // inside the iframe `window.innerHeight` IS the iframe height, so
  // reporting the clamped height would shrink the iframe, which would
  // shrink the clamp, and so on.
  //
  // The callback is read via ref so the effect's deps don't include the
  // whole `env` object. If we put `env` in the deps and the host passes
  // an inline `user` / `configOverride` to `<WexioWidget>`, the env's
  // `useMemo` churns on every host re-render — including window-resize-
  // triggered re-renders — and this effect would fire `onResize` over
  // and over with unchanged dimensions, churning the host page. With the
  // ref pattern, `onResize` only fires when one of the dimension inputs
  // actually changes (open ↔ closed ↔ expanded).
  const onResizeRef = useRef(env.onResize);
  onResizeRef.current = env.onResize;
  const onHitAreaRef = useRef(env.onHitArea);
  onHitAreaRef.current = env.onHitArea;
  // Only the iframe embed drives a host FRAME from the reported size (the
  // in-DOM embeds render fixed on the host page and never clip). So only the
  // iframe needs the measured closed footprint below.
  const isFrameHost = env.kind === "iframe";
  // True from the moment the panel opens until the closed footprint has been
  // reported after it closes (the iframe waits for the exit fade; see below).
  const closeReportPendingRef = useRef(false);
  // Holds a collapse-while-open frame shrink back until the panel's spring has settled.
  const openSizeReporterRef = useRef(createOpenSizeReporter());
  // biome-ignore lint/correctness/useExhaustiveDependencies: effectivePeek / totalUnread / news.hasUnread / the auth popup flag are intentional re-measure triggers — the closed footprint is read from the DOM they drive (peek appearing, badge count changing), so each change must kick a fresh settle, even though they aren't referenced in the body.
  useEffect(() => {
    // Open → the panel's ideal size (loader/host clamps to viewport). Reports
    // ideal, not clamped, to avoid a feedback loop (see the long note above).
    if (isOpen) {
      // iframe: the frame must also hold the panel's anchoring margins (16px
      // side + 16px top + the 96px launcher lane below), otherwise the panel
      // is squeezed to `frame - margins` — a smaller copy of the web-component
      // panel. In-DOM embeds anchor to the page, so they report the ideal.
      closeReportPendingRef.current = true;
      if (isFrameHost) {
        // The open panel uses its whole frame: no clipping.
        onHitAreaRef.current?.(null);
        // Panel margins + its drop shadow (see `openFrameSize`). A collapse is reported only
        // after the panel's spring has settled, else the instantly-shrunk frame clips it.
        return openSizeReporterRef.current.report(
          openFrameSize(idealW, idealH, { marginX: PANEL_MARGIN_X, marginY: PANEL_MARGIN_Y }),
          (width, height) => onResizeRef.current(width, height, true),
        );
      }
      onResizeRef.current(idealW, idealH, true);
      return;
    }
    openSizeReporterRef.current.forget();
    // Closed, in-DOM embeds (web-component / React): the launcher is a fixed
    // element on the host page — nothing clips its shadow/badge/peek — so a
    // nominal box is enough for hosts that size a container from this report.
    if (!isFrameHost) {
      onResizeRef.current(88, 88, false);
      return;
    }
    // Closed, iframe: the iframe is an opaque rectangle that CLIPS whatever
    // overflows it — the drop shadow, the unread badge (overflows the FAB),
    // and especially the peek bubble (content-sized: up to 300px wide and
    // arbitrarily tall). A fixed box can't fit a dynamic peek, so MEASURE the
    // real footprint (FAB + peek) from the viewport corner and report that,
    // growing the frame up/across to fit. The loader pins the closed frame to
    // the viewport corner, so the shadow BELOW/RIGHT the FAB clips at the
    // screen edge exactly like the in-DOM embeds; SHADOW_PAD only pads the
    // GROWN (top/opposite) sides for the shadow + the 1.05 hover scale, which
    // both extend past getBoundingClientRect.
    const SHADOW_PAD = 32;
    let lastW = 0;
    let lastH = 0;
    let lastHit = "";
    // Just closed: keep the OPEN size until the panel has faded out. Reporting
    // the small closed box at the click shrank the frame while the panel was
    // still visible and chopped it into the corner (the frame clips overflow).
    let exiting = closeReportPendingRef.current;
    const measure = () => {
      if (typeof window === "undefined" || exiting) return;
      // By element identity (registered by the launcher components), never by attribute:
      // article HTML in the panel could carry `data-wx-fab` too.
      const size = closedFrameExtent(
        closedFootprintBoxes(),
        { width: window.innerWidth, height: window.innerHeight },
        SHADOW_PAD,
        launcherLeft ? "left" : "right",
      );
      if (size && (size.width !== lastW || size.height !== lastH)) {
        lastW = size.width;
        lastH = size.height;
        onResizeRef.current(size.width, size.height, false);
        // The loader drops the clip on a frame resize; always follow with fresh rects.
        lastHit = "";
      }
      reportHitArea(size ?? { width: window.innerWidth, height: window.innerHeight });
    };
    // Clip the closed frame to the launcher / peek / popup surfaces (+ shadow pad), so its
    // transparent rest no longer swallows host-page clicks (see `hitRects`).
    // Rects are for the frame size just reported (`target`), not the current one: right after a
    // close the document is still panel-sized, and rects measured from its top-left landed beside
    // the shrunk frame (the launcher blinked out). The frame is pinned to its bottom corner, so
    // shift by the size change on the anchored axes.
    function reportHitArea(target: { width: number; height: number }) {
      const rects = hitRects(closedHitSurfaces(), HIT_PAD, target, {
        x: launcherLeft ? 0 : target.width - window.innerWidth,
        y: target.height - window.innerHeight,
      });
      const key = JSON.stringify(rects);
      if (rects.length === 0 || key === lastHit) return;
      lastHit = key;
      onHitAreaRef.current?.(rects);
    }
    // The peek springs in (scale 0.88 → ~1.0 overshoot) and springs out on
    // dismiss (AnimatePresence removes the node a beat LATER). getBoundingClient-
    // Rect reflects the transform, so a single read mis-measures mid-animation.
    // Re-measure across a short settle window so the frame tracks the whole
    // pop-in AND shrinks back after the exit node is gone. This effect also
    // re-runs when the peek/badge inputs change (deps), so each change kicks a
    // fresh settle; `measure` dedupes, so only real size changes post.
    let rafId = 0;
    let settleUntil = 0;
    const tick = () => {
      measure();
      if (performance.now() < settleUntil) {
        rafId = requestAnimationFrame(tick);
      } else {
        rafId = 0;
      }
    };
    const settle = () => {
      settleUntil = performance.now() + 600;
      if (!rafId) rafId = requestAnimationFrame(tick);
    };
    const cancelExitWait = exiting
      ? whenPanelHidden(
          () => panelRef.current,
          () => {
            exiting = false;
            closeReportPendingRef.current = false;
            settle();
          },
        )
      : null;
    if (!exiting) settle();
    // ResizeObserver catches the FAB/peek box changing (e.g. a peek image
    // loading after the settle window closed). Guarded: absent in SSR + the
    // jsdom test env, where the settle pass alone is enough.
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(settle) : null;
    if (ro) {
      for (const kind of CLOSED_ANCHOR_KINDS) {
        const el = getAnchor(kind);
        if (el) ro.observe(el);
      }
    }
    window.addEventListener("resize", settle);
    return () => {
      cancelExitWait?.();
      if (rafId) cancelAnimationFrame(rafId);
      ro?.disconnect();
      window.removeEventListener("resize", settle);
    };
  }, [
    isFrameHost,
    isOpen,
    idealW,
    idealH,
    effectivePeek,
    badgeUnread,
    news.hasUnread,
    showAuthError && authPopupOpen,
    launcherLeft,
  ]);

  // Report the launcher's anchor side to the embed once `launcherPosition`
  // resolves. Only the iframe adapter acts on it (repositions the frame); React
  // / web-component anchor via CSS and leave `onLauncherPosition` unset. Ref
  // pattern mirrors `onResize` — keeps the (now-stable) `env` out of the deps.
  const onLauncherPositionRef = useRef(env.onLauncherPosition);
  onLauncherPositionRef.current = env.onLauncherPosition;
  useEffect(() => {
    onLauncherPositionRef.current?.(launcherLeft ? "left" : "right");
  }, [launcherLeft]);

  // Launcher offset: host offset > `theme.launcherOffset` > 0 → `--wx-off-*` (in-DOM
  // embeds) or reported to the loader (iframe). See `useLauncherOffset`.
  useLauncherOffset(env, config.theme?.launcherOffset);

  const close = () => setIsOpen(false);

  // RTL locales (ar, he) lay the widget out right to left in every embed.
  useLocaleDirection(locale, env.themeRoot);

  // Keyboard: focus into the panel on open, back to the launcher on close, Escape closes.
  const panelRef = useRef<HTMLDivElement>(null);
  usePanelFocus({ isOpen, panelRef, onEscape: close, scopeToWidget: env.kind !== "iframe" });

  // Bulk-read acknowledgement is owned by MessagesTab — it knows whether the
  // new inbound is actually in view (bottom of the conversation) vs scrolled
  // out of sight. The shell passes `openThreadUnread` + `clearOpenThreadUnread`:
  // the tab fires the `markChatRead` op at the right moment and optimistically
  // zeroes the open thread's live unread so the badge/peek drop immediately,
  // before the `threadUpdated{0}` echo reconciles it.

  /**
   * Launcher peek → quick-reply tap. In real mode we send the
   * payload via the visitor mutation; in demo mode we just open the
   * panel (the messages tab's existing demo simulator will react to
   * the visitor's reply once they're inside). Both paths open the
   * widget so the visitor sees their reply land.
   */
  const handlePeekButton = async (payload: string, text: string) => {
    if (peekIsSlot) {
      launcherActions.onBubbleReply(text);
      return;
    }
    // Which thread the peek belongs to. The per-chat SSE pump sets
    // `peekTargetRef` in live mode, but it's disabled in demo — so fall back to
    // the peeked thread itself (also the source `handlePeekOpen` uses). Without
    // this fallback the tap lands on the bare threads-list instead of the thread.
    const target =
      peekTargetRef.current ??
      (peekThread ? { chatId: peekThread.chatId || null, threadKey: peekThread.threadKey } : null);
    if (sessionDummy) {
      // Demo: hand the reply to the messages tab to "send" on mount
      // (visitor bubble + canned response), and clear the peek now so
      // it doesn't resurface after the visitor closes the widget.
      setPendingPeekReply(text);
      setDismissedPeekKey(peekKey);
    } else if (session.chatId) {
      // Optimistic bubble first: the opened conversation shows the tap at once, then the row is
      // swapped for the BE id on ack (same as an in-thread chip tap).
      const optimisticId = `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      appendLiveMessage(peekVisitorBubble(optimisticId, text));
      try {
        // Carry the peeked thread's key so the quick-reply lands on THAT thread (a topic-thread
        // peek reply must not fall back to the main chat) and the button value so a flow resumes.
        const res = await sendVisitor({
          variables: { input: peekSendInput(text, payload, target?.threadKey ?? null) },
        });
        const realId = res.data?.sendVisitorMessage?.messageId;
        if (realId) {
          replaceLiveMessage(optimisticId, {
            ...peekVisitorBubble(optimisticId, text),
            id: realId,
            deliveryStatus: "DELIVERED",
          });
        }
      } catch {
        // Quietly swallow: the visitor retries from inside the panel; a toast at the peek level
        // would be more noise than help.
      }
      // Real mode: MessagesTab mounts with `unreadCount > 0`, sees the
      // new message is at the bottom, and acks via `markChatRead` +
      // clearUnread. The peek is hidden by the `isOpen` gate either way.
    }
    // Land in the thread the peek belonged to (not the bare list).
    if (target?.chatId) {
      didResumeMessagesRef.current = true;
      setRoute({ kind: "messages-thread", chatId: target.chatId, threadKey: target.threadKey });
    } else {
      setRoute({ kind: "messages" });
    }
    openPanel();
    // Quick-reply tap is the visitor's reply — treat as an outbound
    // signal so the operator dashboard's "visitor responded" badge
    // catches it without waiting for the SSE round-trip.
    playOutbound();
  };

  const handlePeekOpen = () => {
    if (peekIsSlot) {
      launcherActions.onBubbleOpen();
      return;
    }
    // Open the thread the peek belongs to (the most-recent unread thread), not
    // the bare list. `didResumeMessagesRef` is marked so the resume effect
    // doesn't then override this with the latest thread.
    if (peekThread) {
      // Opens the peeked thread — a real one, or the synthesised DEFAULT thread
      // (chatId ""), which routes to the main thread and gets marked read by the
      // effect that watches the open route.
      didResumeMessagesRef.current = true;
      setRoute({
        kind: "messages-thread",
        chatId: peekThread.chatId || undefined,
        threadKey: peekThread.threadKey,
      });
    } else {
      setRoute({ kind: "messages" });
    }
    openPanel();
  };

  // Branding gate is server-authoritative: `config.branding` is always
  // resolved from the BE by publicKey (the full fetch, or the lite
  // `?view=gate` fetch when presentation is injected — never from
  // host-injected props), and the BE plan-gates `hidden`. So a host can't
  // fake white-label removal.
  const showBrandingFooter = !config.branding?.hidden;
  // When only ONE tab is enabled the bottom nav adds no value (the
  // visitor cannot move "across") — hide it. Also hides the
  // Messages-header back arrow downstream, since there is nowhere
  // for it to pop to.
  const tabCount = enabledTabCount(config.features);
  const showTabs = routeShowsTabs(route) && tabCount > 1;
  const isHome = route.kind === "home";
  const activeTab = routeToTab(route);

  // Opening the News tab marks the latest post as read → clears the dot.
  // Re-runs when `markRead` changes (i.e. once the latest id has loaded), so
  // a visitor who opened News before the id resolved still gets marked read.
  useEffect(() => {
    if (activeTab === "news") news.markRead();
  }, [activeTab, news.markRead]);

  const switchTab = (next: WidgetTab) => {
    // Mark the tab swap as a transition so the incoming tab's content
    // render (Apollo first-paint, list reconciliation) is interruptible
    // and yields to the slide animation's frames — the old tab stays
    // interactive until the new one is ready, then both the nav
    // highlight + the slide commit together. Keeps the route transition
    // off the animation's critical 16ms budget. See the perf notes in
    // the route `AnimatePresence` below (mode="sync").
    startTransition(() => {
      // Messages: the FIRST open after a reload resumes to the latest thread
      // (drill straight into the conversation skeleton); in-session opens after
      // that land on the list. Other tabs route normally.
      // With MESSAGE slots the Messages tab lands in the page's slot thread (else the last, else a new chat).
      setRoute(
        next === "messages"
          ? (resumeRoute() ??
              (slotLauncher ? launcherActions.messagesEntryRoute() : messagesEntryRoute()))
          : { kind: next },
      );
    });
  };

  // Don't render ANY widget chrome (launcher, panel, peek, popups)
  // until BOTH config has resolved AND the initial visitor-auth
  // handshake has settled (success, error, or skipped in demo /
  // preview). Before that:
  //   - Theme tokens, status (disabled / plan_blocked), and Turnstile
  //     config aren't known yet — paint would flicker.
  //   - The launcher would render with a spinner that resolves to the
  //     chat icon within ~100-300ms, which reads as a glitchy
  //     entrance for the visitor.
  // The whole pre-ready window is fast enough that visitors just see
  // the launcher arrive in its final state on first paint.
  // `authReady` is a one-way latch — once we've ever passed the auth
  // gate (handshake settled with a token OR an error, or the mode
  // doesn't require auth), stay ready for the rest of this mount.
  // Without the latch, tapping the auth-error popup's Retry button
  // briefly clears `session.error` and sets `isAuthenticating=true`
  // simultaneously, which momentarily falls back to "not ready" and
  // unmounts the entire widget — visitors see a full reload flicker
  // instead of just the launcher spinning. The launcher's spinner
  // state (`showAuthSpinner`) is driven by `session.isAuthenticating`
  // and still shows during the retry; we just don't want to throw
  // away the surrounding tree to display it.
  const hasReachedAuthReady = useRef(false);
  const justBecameReady =
    sessionDummy || mode !== "production" || !!session.token || !!session.error;
  if (justBecameReady) hasReachedAuthReady.current = true;
  const authReady = hasReachedAuthReady.current || justBecameReady;
  // Invalid `publicKey` (404 / 403 from `/api/web/config`) — the host
  // wired a key the BE doesn't recognise. Render nothing rather than
  // silently fall back to demo content on a real visitor's page; the
  // empty rectangle is the operator's signal to fix their config. A
  // legitimate "no publicKey" demo (marketing page) keeps `error: null`
  // and continues to show the bundled demo content.
  if (hidden) return null;
  if (configLoading || !authReady) return null;
  // BE kill switch / plan gate: `disabled` (operator turned the widget off) and
  // `plan_blocked` (org below the required plan) both mean "render nothing" —
  // silently, no visitor-visible reason (see the BE `WebConfigStatus` contract).
  // The script-tag loader already gates on this before creating its iframe, but
  // the React and web-component embeds mount this shell directly, so without the
  // check they rendered a live launcher whose session the BE then refused — a
  // widget on the host's site that opens straight into an error. `status` is
  // BE-owned (never taken from host props); a gate that cannot be fetched hides
  // the widget unless an earlier `active` answer is cached (see `hidden` above).

  return (
    <OutboxProvider value={outboxEnabled ? outboxContextValue : null}>
      <SessionContentProvider value={sessionContent}>
        <LocalSlotsProvider value={localSlots}>
          <TrackingProvider
            tracking={config.tracking}
            enabled={mode === "production" && !isDummy}
            token={session.token}
          >
            <motion.div
              aria-hidden={!isOpen}
              // When closed the panel is only visually hidden (opacity 0 +
              // pointer-events none), so its nav/links would stay in the tab order
              // inside an aria-hidden element. `inert` pulls the whole subtree out of
              // focus + the a11y tree while closed.
              inert={!isOpen}
              initial={false}
              animate={
                // Bottom-right bubble at the desktop dimensions on every
                // viewport. The className's `max-w` shrinks the rendered
                // width on narrow phones without overriding the inline
                // `width: 420` (CSS allows both — `max-width` clamps the
                // declared width regardless of source specificity).
                isOpen
                  ? {
                      opacity: 1,
                      y: 0,
                      scale: 1,
                      pointerEvents: "auto",
                      width: panelW,
                      height: panelH,
                    }
                  : {
                      opacity: 0,
                      y: 16,
                      scale: 0.98,
                      pointerEvents: "none",
                      width: panelW,
                      height: panelH,
                    }
              }
              transition={{
                // Open/close rises gently from the launcher on a soft spring.
                //
                // Width + height share ONE spring with identical physics, so an
                // expand/collapse grows BOTH dimensions in lockstep — smooth and
                // proportional. The previous 140 ms linear-ish tween made the
                // height appear to finish before the width: on a short viewport
                // the expanded height meets its `max-h` clamp partway, so a
                // constant-velocity tween hit that ceiling abruptly while the
                // width kept travelling. A spring decelerates into place, so the
                // clamp reads as a settle rather than a hard cut, and the two
                // axes track together regardless of how far each one moves.
                opacity: { type: "spring", stiffness: 320, damping: 30 },
                y: { type: "spring", stiffness: 320, damping: 30 },
                scale: { type: "spring", stiffness: 320, damping: 30 },
                width: { type: "spring", stiffness: 300, damping: 32 },
                height: { type: "spring", stiffness: 300, damping: 32 },
              }}
              data-wx-panel
              // A labelled landmark (not `dialog`: the peek, pickers and lightbox are dialogs and
              // `usePanelFocus` treats any other open dialog as owning Escape).
              role="region"
              aria-label={tLauncher("panel")}
              ref={panelRef}
              // Programmatic focus target on open (see `usePanelFocus`); not a tab stop.
              tabIndex={-1}
              // Host offset (px) from the corner — matches the launcher FAB + peek
              // so the whole widget shifts together. `--wx-off-*` default 0. The
              // viewport caps subtract the SAME offset so pushing the panel up/in
              // shrinks its max size instead of overflowing the opposite edge.
              style={{
                marginBottom: "var(--wx-off-y,0px)",
                ...(launcherLeft
                  ? { marginLeft: "var(--wx-off-x,0px)" }
                  : { marginRight: "var(--wx-off-x,0px)" }),
                maxWidth: embedded
                  ? "calc(100% - 2rem - var(--wx-off-x,0px))"
                  : "calc(100vw - 2rem - var(--wx-off-x,0px))",
                maxHeight: embedded
                  ? "calc(100% - 7rem - var(--wx-off-y,0px))"
                  : "calc(100dvh - 7rem - var(--wx-off-y,0px))",
              }}
              className={cn(
                // `isolate` (isolation: isolate) makes the panel a PERMANENT
                // stacking context. Without it the panel is only a stacking
                // context while `motion` has a `transform` applied (during the
                // open animation) — once that settles to `transform: none` the
                // home gradient's `-z-10` child escapes to the parent context
                // and gets painted BEHIND the panel's opaque `bg-wx-bg`, so it
                // flashes in then vanishes.
                "isolate flex flex-col overflow-hidden bg-wx-bg text-wx-fg outline-none",
                // The panel takes focus on a keyboard open; show it for the keyboard only.
                "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-solid focus-visible:outline-wx-primary",
                // Anchored to the operator's chosen corner (`launcherPosition`),
                // bottom-right by default. `max-w` / `max-h` clamp the bubble
                // with a margin — no `min-width` anywhere so motion's inline
                // `width: 420` can shrink down freely.
                //
                // `embedded` (React-component embed) → anchor to the host
                // `<div>` with `absolute` + %-based clamps so the panel stays
                // inside its container. Otherwise → `fixed` + vw/dvh clamps so
                // it owns the viewport corner (iframe / web-component embeds).
                //
                // `data-wx-panel` opts the element into the theme-aware
                // shadow + frame-glow rules in globals.css (light: drop
                // shadow; dark: hairline ring + radial glow blob + stronger
                // shadow).
                embedded ? "absolute" : "fixed",
                // One above the launcher so the panel covers it when open.
                // Same max-int range as the launcher → stays above any host
                // page chrome (sticky headers, modals, banners, …).
                "bottom-24 z-2147483601 rounded-wx-xl",
                // max-w / max-h (offset-aware) live in the inline `style` above.
                launcherLeft ? "left-4" : "right-4",
              )}
            >
              {isHome && <HomeBackdrop glow={config.theme?.homeBackdropGlow !== false} />}

              {/* Per-route sticky header. Home has no sticky header — its
            chrome is rendered inside the scrollable content area.

            Called as a plain render FUNCTION (not `<RouteHeader/>`): it is
            declared inside this component, so rendering it as a JSX element
            would mint a new component TYPE on every render, and React would
            UNMOUNT + REMOUNT the whole header subtree each time the shell
            re-renders. A widget-wide `threadUpdated` SSE frame (a typing ping
            from the other party decays/updates `threadLive` every few seconds)
            re-renders the shell — the remount discarded the header overflow
            (⋮) menu's local `open` state, so it snapped shut the instant the
            visitor opened it while someone was typing. Invoking the helper
            inlines its returned element at a STABLE tree slot, so the header
            keeps its identity (and the menu stays open) across re-renders,
            while a real route change still swaps the element type and remounts
            as intended. The helper holds no hooks, so a plain call is safe. */}
              {renderRouteHeader({
                route,
                onPop: () => setRoute(popRoute(route, config.features)),
                onClose: close,
              })}

              {/* `relative` so the absolutely-anchored route layers stack
            inside the main area. Both exiting and entering routes
            occupy `inset-0` so the panel height never jumps mid-
            transition. */}
              <main className="relative flex flex-1 flex-col overflow-hidden">
                {/* Defer mounting tab content until the visitor has
              opened the panel at least once. The launcher unread
              query + visitor-auth handshake already cover what we
              need while closed; deferring everything else keeps the
              initial page-load footprint to one query (`visitorChatUnread`)
              instead of all four tabs racing to fetch their own
              data. After the first open, `hasOpened` stays true and
              the standard mount/unmount of routed tabs takes over. */}
                {hasOpened && (
                  // `mode="sync"` (not `popLayout`): the routes are
                  // `absolute inset-0` and cross-slide via a `transform` — there
                  // are no siblings to reflow, so popLayout's per-frame FLIP
                  // *layout* measurement (the bulk of the animation cost in
                  // profiling: ~1.2s of `serviceScriptedAnimations`) was pure
                  // overhead, and popLayout kept the *outgoing* heavy tab
                  // re-rendering as it exited. `sync` overlaps enter+exit on
                  // transform/opacity only — same visual, compositor-driven.
                  <AnimatePresence mode="sync" initial={false} custom={direction}>
                    <RouteLayer
                      key={routeKey(route)}
                      custom={direction}
                      variants={routeVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      // Tween (not spring) for two reasons:
                      //   1. Spring simulators re-render every frame until
                      //      the velocity settles below threshold — for the
                      //      visitor that's invisible work paid for *every*
                      //      route change. A 180 ms ease-out covers the
                      //      visible distance in fewer integration steps at
                      //      lower CPU, and the result looks identical at
                      //      this scale (28 px is too small for spring's
                      //      overshoot to read).
                      //   2. The outgoing route keeps re-rendering as React
                      //      still owns it during the exit — a heavy tab
                      //      (Messages with its live query) burns frames while
                      //      exiting. The short tween caps that exit work to
                      //      ~11 frames at 60 fps (and `startTransition` on the
                      //      tab swap keeps the *incoming* render off these
                      //      frames).
                      transition={{
                        x: { type: "tween", duration: 0.18, ease: [0.32, 0.72, 0, 1] },
                        opacity: { duration: 0.14, ease: "easeOut" },
                      }}
                      // `will-change: transform` hoists the slide into its
                      // own GPU compositor layer — paints stay off the main
                      // thread, which is what makes the slide read as
                      // smooth even when the incoming route's first frame
                      // is heavy (Apollo first-paint, large lists, etc.).
                      style={{ willChange: "transform, opacity" }}
                      className="absolute inset-0 flex flex-col overflow-hidden"
                    >
                      {route.kind === "home" && (
                        <HomeTab
                          config={config}
                          organizationLogoUrl={organizationLogoUrl}
                          onClose={close}
                          onOpenTab={(tab) => setRoute({ kind: tab })}
                          onOpenThread={() =>
                            // Recent-message card → open its conversation (the main
                            // thread) directly, not the list. `drillTo` records
                            // Home as origin so Back returns here.
                            drillTo({
                              kind: "messages-thread",
                              chatId: session.chatId ?? undefined,
                              threadKey: null,
                            })
                          }
                          onOpenNewsArticle={(id) => drillTo({ kind: "news-article", id })}
                          onOpenHelpArticle={(slug) =>
                            drillTo({
                              kind: "help-article",
                              slug,
                              from: "search",
                            })
                          }
                          isDummy={isDummy}
                          chatId={session.chatId ?? null}
                        />
                      )}
                      {route.kind === "messages" && (
                        <ThreadList
                          // Live `visitorThreads` (BE Conversation Model Phase 3),
                          // adapted to `ThreadListItem`; mock data in dummy mode.
                          // See the `threads` memo near `threadsQuery`.
                          threads={threads}
                          loading={threadsLoading}
                          // Fetch failed with nothing loaded (offline / BE down) —
                          // threads are private (never cached), so show a "couldn't
                          // load" state + retry instead of the empty state.
                          loadError={!sessionDummy && !!threadsQuery.error && threads.length === 0}
                          onRetry={refetchThreads}
                          avatarUrl={conversationLogoUrl ?? undefined}
                          onOpenThread={(thread) => {
                            drillTo({
                              kind: "messages-thread",
                              chatId: thread.chatId || undefined,
                              threadKey: thread.threadKey,
                            });
                          }}
                          onNewQuestion={handleStartNewConversation}
                          blocked={visitorBlocked}
                          slotThread={slotListThread}
                        />
                      )}
                      {route.kind === "messages-thread" && route.pendingLatest && (
                        <ConversationSkeleton />
                      )}
                      {route.kind === "messages-thread" && !route.pendingLatest && (
                        <MessagesTab
                          // A new session (shutdown) remounts the tab so no composer state survives it.
                          key={sessionEpoch}
                          isDummy={sessionDummy}
                          // Per-thread: an existing thread opens on its own
                          // `chatId` (its history + read); a freshly-minted "new
                          // question" thread (threadKey, no chatId yet) opens an
                          // EMPTY conversation — NOT the main chat. Only the
                          // anchor/main thread (no threadKey) falls back to the
                          // session chat.
                          chatId={route.chatId ?? (route.threadKey ? null : session.chatId)}
                          threadKey={route.threadKey}
                          welcomeSlotId={route.welcomeSlotId}
                          catchUpHistory={!!adoptedChatId && route.chatId === adoptedChatId}
                          onChatIdResolved={(cid) => {
                            // A new-question thread's first send just created it —
                            // adopt the real chatId so history / read / SSE key on
                            // it, refetch the list so the new thread appears, and
                            // bump the epoch so the visitor-level stream reconnects
                            // and starts fanning out this new thread too.
                            setRoute((r) =>
                              r.kind === "messages-thread" && !r.chatId ? { ...r, chatId: cid } : r,
                            );
                            // Mark it just-adopted so the remounted tab catches the
                            // flow greeting that may have raced the SSE subscribe.
                            setAdoptedChatId(cid);
                            refetchThreads();
                            setThreadsEpoch((e) => e + 1);
                          }}
                          token={session.token}
                          prechatForm={config.prechatForm}
                          messenger={config.messenger}
                          organizationLogoUrl={organizationLogoUrl}
                          organizationName={config.messenger?.title ?? ""}
                          operatorAvatars={config.operatorAvatars}
                          isOpen={isOpen}
                          onInbound={playInbound}
                          onOutbound={handleTabOutbound}
                          registerRealtimeHandler={registerMessagesRealtimeHandler}
                          // Per-thread unread (NOT the main-scoped launcher count) so
                          // the read-ack fires for the OPEN topic thread + the unread
                          // divider is correct for it.
                          unreadCount={openThreadUnread}
                          clearUnread={clearOpenThreadUnread}
                          liveMessages={liveMessages}
                          appendLiveMessage={appendLiveMessage}
                          replaceLiveMessage={replaceLiveMessage}
                          enqueueSend={outboxEnabled ? enqueueSend : undefined}
                          onRetryOutbox={outbox.retry}
                          assignmentStatus={assignment.status}
                          assignmentEstimate={assignment.estimate}
                          // CLOSED locks the composer (visitor can only start a new
                          // conversation); RESOLVED keeps it. Derived from the live
                          // thread list so an operator close hides it in real time.
                          threadStatus={openThreadStatus}
                          // Blocked (thread OR global) locks the composer; global
                          // block also disables "start a new conversation".
                          blocked={openThreadBlocked}
                          globalBlocked={visitorBlocked}
                          onStartNewConversation={handleStartNewConversation}
                          initialVisitorMessage={
                            sessionDummy
                              ? pendingPeekReply
                              : pendingSlotReply && pendingSlotReply.threadKey === route.threadKey
                                ? pendingSlotReply.text
                                : null
                          }
                          onInitialVisitorMessageSent={() => {
                            setPendingPeekReply(null);
                            setPendingSlotReply(null);
                          }}
                          visitorIdentity={visitorIdentity}
                          visitorPrefill={visitorPrefill}
                          security={config.security}
                          verified={session.verified}
                          identified={identifiedVisitor}
                          sessionName={identifiedVisitor ? session.displayName : null}
                          onGoogleCredential={(idToken) =>
                            setWidgetIdentity({ googleIdToken: idToken })
                          }
                          onPasskeySession={(result: PasskeySessionResult) => {
                            // Persist so a reload restores this verified session
                            // (passkey can't be silently re-proven). Also drive
                            // the immediate (pre-reload) UI via the override.
                            persistIdentifiedSession(result, instance.publicKey, instance.storage);
                            setPasskeySession({
                              token: result.token,
                              chatId: result.chatId,
                              peopleId: result.peopleId,
                              displayName: result.displayName,
                              isAuthenticating: false,
                              error: null,
                              verified: true,
                              // The passkey handshake doesn't carry a block flag; the
                              // threads query + live stream re-establish it on this
                              // session's first fetch (same as a restored session).
                              blocked: false,
                            });
                          }}
                          onInternalLink={(link) => {
                            // AI replies sometimes link to internal articles
                            // (`/help/.../slug` or `/news/slug`). Route them
                            // through the widget's own viewer instead of
                            // following the browser navigation to the widget's
                            // origin (where the path doesn't exist).
                            if (link.kind === "help-article") {
                              drillTo({
                                kind: "help-article",
                                slug: link.slug,
                                from: "search",
                                locale: link.locale,
                              });
                            } else if (link.kind === "news-article") {
                              // Best-effort fallback: drop the visitor on the
                              // news feed. A future improvement is to extend
                              // `NewsArticleView` to accept a slug so we can
                              // open the specific post directly. For now the
                              // visitor lands in the right tab and can find it.
                              setRoute({ kind: "news" });
                            }
                          }}
                          getTurnstileToken={getTurnstileToken}
                        />
                      )}
                      {route.kind === "help" && (
                        <HelpTab
                          isDummy={isDummy}
                          onOpenCategory={(categoryId, meta) =>
                            drillTo({
                              kind: "help-category",
                              categoryId,
                              categoryName: meta?.name,
                              categoryDescription: meta?.description,
                              categoryArticleCount: meta?.articleCount,
                            })
                          }
                          onOpenArticle={(slug) =>
                            drillTo({ kind: "help-article", slug, from: "search" })
                          }
                        />
                      )}
                      {route.kind === "help-category" && (
                        <HelpCategoryView
                          categoryId={route.categoryId}
                          categoryName={route.categoryName}
                          categoryDescription={route.categoryDescription}
                          categoryArticleCount={route.categoryArticleCount}
                          isDummy={isDummy}
                          onOpenCategory={(cid, meta) =>
                            drillTo({
                              kind: "help-category",
                              categoryId: cid,
                              categoryName: meta?.name,
                              categoryDescription: meta?.description,
                              categoryArticleCount: meta?.articleCount,
                            })
                          }
                          onOpenArticle={(slug) =>
                            drillTo({
                              kind: "help-article",
                              slug,
                              from: "category",
                              fromCategoryId: route.categoryId,
                            })
                          }
                        />
                      )}
                      {route.kind === "help-article" && (
                        <Suspense fallback={<RouteChunkFallback />}>
                          <HelpArticleView
                            slug={route.slug}
                            // Honour a language-selector override (slug + the
                            // chosen variant's locale); otherwise the widget
                            // locale drives the lookup.
                            locale={route.locale ?? locale}
                            isDummy={isDummy}
                            onHandoff={() => setRoute({ kind: "messages" })}
                            onOpenArticle={(s: string) =>
                              setRoute((prev) =>
                                openRelatedArticle(
                                  { kind: "help-article", slug: s, from: "search" },
                                  prev,
                                ),
                              )
                            }
                            onOpenTranslation={(s: string, loc: string) =>
                              // Switch language in place — keep the same back-nav
                              // context (same `origin`) so "Back" still lands where
                              // the visitor came from, without inserting the
                              // pre-switch article as an extra back step.
                              setRoute({
                                kind: "help-article",
                                slug: s,
                                locale: loc,
                                from: route.from,
                                fromCategoryId: route.fromCategoryId,
                                origin: route.origin,
                              })
                            }
                            onTitleResolved={articleTitleSink}
                          />
                        </Suspense>
                      )}
                      {route.kind === "news" && (
                        <NewsTab
                          isDummy={isDummy}
                          onOpenArticle={(id) => drillTo({ kind: "news-article", id })}
                        />
                      )}
                      {route.kind === "news-article" && (
                        <Suspense fallback={<RouteChunkFallback />}>
                          <NewsArticleView
                            id={route.id}
                            isDummy={isDummy}
                            onOpenRelated={(rid) =>
                              setRoute((prev) =>
                                openRelatedArticle({ kind: "news-article", id: rid }, prev),
                              )
                            }
                            onTitleResolved={articleTitleSink}
                          />
                        </Suspense>
                      )}
                      {route.kind === "profile" && (
                        <ProfileTab
                          // A new person (shutdown) gets a fresh form; no fetch while the new session's
                          // first handshake is still in flight (it would go out with no token).
                          key={shutdownEpoch}
                          isDummy={sessionDummy}
                          pending={
                            !sessionDummy &&
                            mode === "production" &&
                            !session.token &&
                            session.isAuthenticating
                          }
                        />
                      )}
                    </RouteLayer>
                  </AnimatePresence>
                )}
              </main>

              {showTabs && (
                <BottomNav
                  active={activeTab}
                  features={config.features}
                  onChange={switchTab}
                  unreadCount={totalUnread}
                  newsUnread={news.hasUnread}
                />
              )}
              {/* The panel's very last line — normally the "Powered by Wexio"
            badge, but the offline/online CONNECTION STATUS takes over the same
            slot when it matters (widget-wide, every tab). On list routes it
            lands under the tab nav; on drill-in routes under the composer /
            content. The status shows even when branding is white-label-hidden. */}
              <ConnectionFooter online={online} showBranding={showBrandingFooter} />
            </motion.div>
          </TrackingProvider>
        </LocalSlotsProvider>
      </SessionContentProvider>

      <LauncherPeek
        message={!isOpen ? effectivePeek : null}
        onOpen={handlePeekOpen}
        onDismiss={() =>
          peekIsSlot ? launcherActions.onBubbleDismiss() : setDismissedPeekKey(effectivePeekKey)
        }
        onSendButton={handlePeekButton}
        position={launcherLeft ? "left" : "right"}
        embedded={embedded}
      />

      {/* Floating auth-error popup above the launcher. Opens only
          when the visitor taps the launcher during an auth error (so
          a passive failure doesn't shout at them); has its own X chip
          so they can dismiss without resolving — re-tapping the
          launcher brings it back. Auto-closes when the error
          resolves. Same lane as `LauncherPeek` (they never both
          show at once: peek needs a working session to surface). */}
      <AuthErrorPopup
        open={showAuthError && authPopupOpen}
        errorCode={session.error}
        onRetry={handleAuthRetry}
        onDismiss={dismissAuthPopup}
        position={launcherLeft ? "left" : "right"}
        embedded={embedded}
      />

      <WidgetLauncher
        isOpen={isOpen}
        onToggle={launcherActions.onLauncherToggle}
        unreadCount={badgeUnread}
        newsUnread={news.hasUnread}
        logoUrl={config.showLogoInLauncher ? organizationLogoUrl : null}
        position={launcherLeft ? "left" : "right"}
        embedded={embedded}
        loading={showAuthSpinner}
      />
    </OutboxProvider>
  );

  // Render helper (NOT a component — see the call site). Lower-cased + invoked
  // as a function so its output inlines at a stable tree slot and survives
  // shell re-renders without remounting. Holds no hooks of its own.
  function renderRouteHeader({
    route,
    onPop,
    onClose,
  }: {
    route: WidgetRoute;
    onPop: () => void;
    onClose: () => void;
  }) {
    if (route.kind === "messages") {
      // The Messages tab lands on the thread LIST — a plain tab-title header
      // (no back chip; the bottom nav is shown for a list surface).
      return <TabTitleHeader title={tHeader("messagesTitle")} onClose={onClose} />;
    }
    if (route.kind === "messages-thread") {
      // A single topic thread's conversation. Back always returns to the
      // thread list (which always exists), so `onBack` is unconditional.
      // Operator can override the header title + subtitle via
      // `config.messenger`; falls back to the org name / i18n subtitle.
      const messengerTitle = config.messenger?.title?.trim() || AI_ASSISTANT_NAME;
      const messengerSubtitle =
        config.messenger?.description?.trim() || tHeader("conversationSubtitle");
      // The open conversation's chatId (a keyed topic thread carries its own;
      // otherwise the main anchor). `null` for a not-yet-created thread — no
      // transcript to export yet, so the download button hides.
      const openChatId = route.chatId ?? (route.threadKey ? null : session.chatId);
      return (
        <ConversationHeader
          title={messengerTitle}
          subtitle={messengerSubtitle}
          organizationLogoUrl={conversationLogoUrl}
          muted={muted}
          onToggleMute={toggleMuted}
          onBack={onPop}
          onClose={onClose}
          expanded={effectiveExpanded}
          onToggleExpand={toggleExpand}
          showResolve={assignment.status === "connected"}
          onResolve={handleResolve}
          onDownloadTranscript={
            !sessionDummy && openChatId ? () => handleDownloadTranscript(openChatId) : undefined
          }
          downloadingTranscript={transcriptDownloading}
        />
      );
    }
    if (route.kind === "help") {
      return <TabTitleHeader title={tHeader("helpTitle")} onClose={onClose} />;
    }
    if (route.kind === "help-category") {
      return <TabTitleHeader title={tHeader("helpTitle")} onBack={onPop} onClose={onClose} />;
    }
    if (route.kind === "help-article") {
      return (
        <TabTitleHeader
          // Show the article's title once it has loaded — falls back
          // to the parent-tab nameplate while the data is in flight.
          title={articleTitle ?? tHeader("helpTitle")}
          onBack={onPop}
          onClose={onClose}
          expanded={effectiveExpanded}
          onToggleExpand={toggleExpand}
        />
      );
    }
    if (route.kind === "news") {
      return <TabTitleHeader title={tHeader("newsTitle")} onClose={onClose} />;
    }
    if (route.kind === "news-article") {
      return (
        <TabTitleHeader
          title={articleTitle ?? tHeader("newsTitle")}
          onBack={onPop}
          onClose={onClose}
          expanded={effectiveExpanded}
          onToggleExpand={toggleExpand}
        />
      );
    }
    return null; // home — no sticky header
  }
}
